import { ActionType } from 'typesafe-actions';
import { all, call, delay, fork, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { uniq } from 'ramda';

import {
  knmFillOrganizationsAndObjectsByOtherPassport,
  knmGetSuccess,
  knmPatchRawData,
  knmPlanFindGet,
  knmSetInitialAccessSettingsFormValues,
  knmUpdateEvidencesByDecisionLinks,
} from 'KnmPackage/MainModule/actions/knmActions';
import { KnmActionsEnum } from 'KnmPackage/MainModule/enums/KnmActionsEnum';
import { dictionariesItemValueGetSuccess } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { IKnmResultDecision248 } from 'APIPackage/KnmModule/interfaces/IKnmResultDecision248';
import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { createKnmTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmTypeSelectOption';
import {
  getKnmData,
  getKnmFormRef,
  getKnmSystemType,
  getKnmTypesCanceled,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { createKnmReasonExtendedSelectOption } from 'CorePackage/CatalogsModule/common/createKnmReasonTypeSelectOption';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { createKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmDocumentTypeSelectOption';
import { IKnmErknmFormCollaboratingOrganization } from 'KnmPackage/MainModule/interfaces/IKnmErknmFormCollaboratingOrganization';
import { createChecklistAnswerTypeSelectOption } from 'CorePackage/CatalogsModule/common/createChecklistAnswerTypeSelectOption';
import {
  geCatalogsKnmPlanReasonChangeTypesSelectOptions,
  getCatalogKnmRemarkSelectOptions,
  getDistrictSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { registryExportOkvedGet } from 'CorePackage/RegistryModule/actions/registryActions';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { createKnmRejectReasonTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmRejectReasonTypeSelectOption';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { createKnmExpertTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmExpertTypeSelectOption';
import { createKnmNotificationMethodSelectOption } from 'CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';
import { fetchKnmLinks } from 'APIPackage/KnmModule/services/knmAdditionalDataApi';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { getDictionarySelectOptionWithState } from 'CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { getDictionariesItemValue } from 'CorePackage/DictionariesModule/common/getDictionariesItemValue';
import { IKnmDictionary } from 'APIPackage/KnmModule/interfaces/IKnmDictionary';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { AdminActionsEnum } from 'PersonalAccountPackage/AdminModule/enums/AdminActionsEnum';
import {
  adminGetFetchSettings,
  adminGetFetchSettingsSuccess,
} from 'PersonalAccountPackage/AdminModule/actions/adminAction';
import { getFullNameGuarantor } from 'KnmPmPackage/PmReasonsModule/common/getFullNameGuarantor';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { fetchKnm } from 'APIPackage/KnmModule/services/knmApi';
import { IKnmType } from 'APIPackage/CatalogsModule/interfaces/IKnmType';
import { IKnmAbrogationReason } from 'APIPackage/CatalogsModule/interfaces/IKnmAbrogationReason';
import { ICatalog } from 'CorePackage/CatalogsModule/interfaces/ICatalog';
import { fetchDecisionById } from 'APIPackage/DecisionModule/services/decisionApi';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getDecisionEvidences } from 'KnmErknmPackage/ErknmReasonsModule/common/getDecisionEvidences';
import { IProsecutorOffice } from 'APIPackage/KnmModule/interfaces/IProsecutorOffice';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getProsecutorOrganization } from 'functions/getProsecutorOrganization';
import { getInitHash } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { scrollToBlock } from 'CorePackage/MainModule/common/scrollToBlock';
import { setInitHash } from 'CorePackage/HeaderModule/actions/headerActions';
import {
  createDictionaryWaitChannel,
  dictionaryHandlerSaga,
  globalDictionaryTracker,
} from 'functions/dictionaryHandlerSaga';
import { createMandatoryCategorySelectOption } from 'CorePackage/CatalogsModule/common/createMandatoryCategorySelectOption';
import { fetchProsecutorOrganisationsByErvkUuidAndVersion } from 'APIPackage/CatalogsModule/services/prosecutorOrganisationsApi';
import { createUserOrganizationWithDistrictSelectOption } from 'CorePackage/AccountModule/common/createUserOrganizationWithDistrictSelectOption';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { createKnmProsecutorOrganisationSelectOption } from 'KnmPackage/MainModule/common/createKnmProsecutorOrganisationSelectOption';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { findRegionSelectOption } from 'KnmPackage/MainModule/components/regions';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';

import {
  knmErknmSetInitialFormValues,
  knmErknmSetPrescriptionFailed,
  knmErknmSetPrescriptionOldRules,
} from '../actions/knmErknmActions';
import { getKnmErknmInitialFormValues } from '../selectors/knmErknmSelectors';
import { IKnmErknmFormObject } from '../interfaces/IKnmErknmFormObject';
import { DocumentsTypeCodesEnum } from '../enums/DocumentsTypeCodesEnum';
import { IKnmErknmFormInspector } from '../interfaces/IKnmErknmFormInspector';
import { IKnmErknmFormApproveDoc } from '../interfaces/IKnmErknmFormApproveDoc';
import { observedSelectOptions } from '../constants/observedSelectOptions';
import { IKnmErknmFormChangePlanReason } from '../interfaces/IKnmErknmFormChangePlanReason';
import { appealStatusSelectOptions } from '../constants/appealStatusSelectOptions';
import {
  approvedChangeKnmSelectOptions,
  approvedSelectOptions,
  approvedSelectOptionsOld,
  planFullListTypeApprovedSelectOptions,
  planFullListTypeApprovedSelectRenamedOptions,
  planTypeRejectAppealedSelectOptions,
} from '../constants/approvedSelectOptions';
import {
  approveRequiredSelectOption,
  getApproveRequiredSelectOptionsByCreated,
} from '../constants/approveRequiredSelectOptions';
import { IKnmErknmFormOrganizationDocument } from '../interfaces/IKnmErknmFormOrganizationDocument';
import { IKnmErknmFormPlace } from '../interfaces/IKnmErknmFormPlace';
import { IKnmErknmFormSuspensionEvent } from '../interfaces/IKnmErknmFormSuspensionEvent';
import { createPlanSelectOption } from '../common/createPlanSelectOption';
import { convertKnmDataJudgmentsToFormValues } from '../common/convertKnmDataJudgmentsToFormValues';
import { convertKnmDataAccessPrivilegesToFormValues } from '../../../KnmPackage/MainModule/common/convertKnmDataAccessPrivilegesToFormValues';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { createInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/common/createInteractionCategorySelectOption';
import { createKnmEventInspectorSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmEventInspectorSelectOption';
import { IKnmNotificationsForm } from '../../../KnmPackage/KnmNotificationModule/interfaces/IKnmNotificationsForm';
import { IErvkCheckList } from '../../../APIPackage/DictionariesModule/interfaces/IErvkCheckList';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { fetchDictionaryValue } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';
import { parseDictionaryVersionValue } from '../../../CorePackage/DictionariesModule/common/parseDictionaryVersionValue';
import { IDictionaryVersion } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { IReasonRequirements } from '../../../APIPackage/DictionariesModule/interfaces/IReasonRequirements';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { CHECKING_IT_ACCREDITATION } from '../../../CorePackage/MainModule/constants/notices';
import { IKnmDistrictSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { CatalogsActionsEnum } from '../../../CorePackage/CatalogsModule/enums/CatalogsActionsEnum';
import { catalogGetSuccess } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IDictionaryPermitDocSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPermitDocSelectOption';
import { fetchKnmAbrogationLinks } from '../../../APIPackage/KnmModule/services/knmAbrogationDataApi';
import { convertSuspensionEventsToFormData } from '../common/convertSuspensionEventsToFormData';
import { sortSuspensionEvents } from '../common/sortSuspensionEvents';
import { getInitialObject } from '../common/getInitialObject';
import { IKnmErknmFormEventInspector } from '../interfaces/IKnmErknmFormEventInspector';
import { IKnmErvk } from '../../../APIPackage/CatalogsModule/interfaces/IKnmErvk';
import { convertKnmDataConditionTypeToFormValue } from '../../../KnmPackage/KnmDocumentsModule/common/convertKnmDataConditionTypeToFormValue';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { createSubmittedDocuments } from '../../../KnmPackage/MainModule/common/createSubmittedDocuments';
import { getDocTypeOption } from '../../../KnmPackage/KnmEventSubjectModule/common/docTypeGetFunctions';
import { getKindControlLabel } from '../../../KnmPackage/MainModule/common/getKindControlLabel';
import { getKindControlSelectedLabel } from '../../../KnmPackage/MainModule/common/getKindControlSelectedLabel';
import { getSupervisionDictionaryValue } from '../../../CorePackage/DictionariesModule/common/getSupervisionDictionaryValue';
import { IKnmRemarkSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmRemarkSelectOption';
import { getOrganizations } from './getOrganizations';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { IFederalDistrict } from '../../../APIPackage/KnmModule/interfaces/IFederalDistrict';
import { convertAgreementsDataToFormValues } from '../common/convertAgreementsDataToFormValues';
import { IErknmFormAgreement } from '../interfaces/IErknmFormAgreement';
import { IKnmErknmViolationEvidence } from '../interfaces/IKnmErknmViolationEvidence';
import { convertKnmDataViolationsToFormValues } from '../common/convertKnmDataViolationsToFormValues';
import { convertKnmApproveRemarksToFormValues } from '../common/convertKnmApproveRemarksToFormValues';
import { isSourceCreatedByOldRules } from '../../../CorePackage/MainModule/common/prescriptionLegacy';

export function* watchKnmErknmSaga() {
  yield takeEvery([KnmActionsEnum.KNM_GET_SUCCESS, KnmActionsEnum.KNM_UPDATE_SUCCESS], knmErknmInit);
  yield takeEvery([KnmActionsEnum.KNM_GET_SUCCESS, KnmActionsEnum.KNM_UPDATE_SUCCESS], knmErknmInitLinksSaga);
  yield takeEvery(
    [
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS,
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_ALREADY_LOAD,
    ],
    knmErknmSetValueByOptionsSaga,
  );
  yield takeEvery(CatalogsActionsEnum.CATALOG_GET_SUCCESS, knmErknmSetValueByOptionsFromCatalogActionsSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_FETCH_SETTINGS_SUCCESS, adminGetFetchSettingsSagaSuccess);
  yield takeEvery(
    KnmActionsEnum.KNM_FILL_ORGANIZATIONS_AND_OBJECTS_BY_OTHER_PASSPORT,
    knmErknmFillOrganizationsAndObjectsByOtherPassportSaga,
  );
  yield takeEvery(KnmActionsEnum.KNM_UPDATE_EVIDENCES_BY_DECISION_LINKS, knmErknmUpdateEvidencesByDecisionLinksSaga);
}

function* adminGetFetchSettingsSagaSuccess(action: ActionType<typeof adminGetFetchSettingsSuccess>) {
  function* funcTry() {
    const { settings } = action.payload;

    const state: IRootState = yield select();
    const currentKnmData = getKnmData(state);

    if (!currentKnmData) {
      return;
    }

    const isShow22 = isLaterThanAdminSetting(
      currentKnmData?.created,
      settings,
      AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM,
    );

    const isPlanType = currentKnmData?.type?.code === KnmTypeCodesEnum.PP;

    yield put(
      knmErknmSetInitialFormValues({
        approved: isPlanType
          ? planFullListTypeApprovedSelectOptions.find(
              item => item.value === currentKnmData?.knmErknm?.approve?.approved,
            )
          : (isShow22 ? approvedSelectOptions : approvedSelectOptionsOld).find(
              item => item.value === currentKnmData?.knmErknm?.approve?.approved,
            ),
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmErknmInitSelectOptionsSaga(action: ActionType<typeof knmGetSuccess>) {
  const { data: knmData } = action.payload;
  const knoOrganization = knmData?.knmErknm?.knoOrganization;
  const supervisionTypeErknm = knmData?.knmErknm?.supervisionTypeErknm;
  const titleSigner = knmData?.knmErknm?.decision?.titleSigner;
  const kindControl = knmData?.knmErknm?.kindControl;
  const kindKnm = knmData?.knmErknm?.kindKnm;
  const approveDocs = knmData?.knmErknm?.approveDocs;
  const collaboratingOrganizations = knmData?.knmErknm?.collaboratingOrganizations;
  const reasons = knmData?.knmErknm?.reasons;
  const events = knmData?.knmErknm?.events;
  const inspectors = knmData?.knmErknm?.inspectors;
  const organizations = knmData?.knmErknm?.organizations;
  const initialObjects = knmData?.knmErknm?.objects;
  const initialChecklists = knmData?.knmErknm?.checklists;
  const initialRequirements = knmData?.knmErknm?.requirements;
  const accessPrivilege = knmData?.knmErknm?.accessPrivilege;
  const initialAgreements = knmData?.knmErknm?.agreements;
  const initialResultDecisions = knmData?.knmErknm?.organizations?.reduce((acc, item) => {
    let newResultDecisions: IKnmResultDecision248[] | undefined;

    if (item.resultDecisions) {
      newResultDecisions = [...item.resultDecisions];
    }

    return newResultDecisions ? [...acc, ...newResultDecisions] : acc;
  }, []);
  const registryData = knmData?.knmErknm?.organizations
    ?.filter(item => item.inn && item.ogrn)
    .map(item => ({
      ogrn: item.ogrn,
      inn: item.inn,
    }));

  if (knoOrganization) {
    yield getDictionariesItemValue(knoOrganization);
  }

  if (supervisionTypeErknm?.dictId) {
    yield getDictionariesItemValue(supervisionTypeErknm);
  }
  if (titleSigner) {
    yield getDictionariesItemValue(titleSigner);
  }
  if (kindControl) {
    if (knoOrganization) {
      yield getSupervisionDictionaryValue(knoOrganization, kindControl);
    } else {
      yield getDictionariesItemValue(kindControl);
    }
  }
  if (kindKnm) {
    yield getDictionariesItemValue(kindKnm);
  }

  const resultArrayActions: unknown[] = [];
  const currentDictionariesFromData: IKnmDictionary[] = [];

  if (registryData) {
    registryData?.forEach(item => {
      const { inn, ogrn } = item;

      if (inn && ogrn) {
        resultArrayActions.push(registryExportOkvedGet({ inn, ogrn }));
      }
    });
  }

  if (approveDocs) {
    approveDocs?.forEach(item => item.approveDoc && currentDictionariesFromData.push(item.approveDoc));
  }
  if (initialAgreements) {
    initialAgreements?.forEach(item => {
      item.titleSigner && currentDictionariesFromData.push(item.titleSigner);
      item.terminate?.titleSigner?.dictId && currentDictionariesFromData.push(item.terminate?.titleSigner);
      item.execution?.titleSigner && currentDictionariesFromData.push(item.execution?.titleSigner);
      item.changes?.forEach(change => change.titleSigner && currentDictionariesFromData.push(change.titleSigner));
    });
  }
  if (collaboratingOrganizations) {
    collaboratingOrganizations?.forEach(
      item => item.organization && currentDictionariesFromData.push(item.organization),
    );
  }
  if (reasons) {
    reasons?.forEach(item => {
      item.riskIndicators?.forEach(
        indicatorValue =>
          indicatorValue.riskIndicator && currentDictionariesFromData.push(indicatorValue.riskIndicator),
      );
    });
  }
  if (initialObjects) {
    initialObjects?.forEach(item => {
      if (item.objectType) {
        currentDictionariesFromData.push(item.objectType);
      }
      if (item.objectKind) {
        currentDictionariesFromData.push(item.objectKind);
      }
      if (item.objectSubKind) {
        currentDictionariesFromData.push(item.objectSubKind);
      }
      if (item.riskCategory) {
        currentDictionariesFromData.push(item.riskCategory);
      }
      if (item.dangerClass) {
        currentDictionariesFromData.push(item.dangerClass);
      }
    });
  }
  if (organizations) {
    organizations?.forEach(item => {
      if (item.act?.titleSigner) {
        currentDictionariesFromData.push(item.act.titleSigner);
      }
      if (item.act?.resultContentPositionCancel) {
        currentDictionariesFromData.push(item.act.resultContentPositionCancel);
      }
      item.act?.knoInspectors?.forEach(
        knoInspector => knoInspector.position && currentDictionariesFromData.push(knoInspector.position),
      );
      item.act?.events?.forEach(event => event.event && currentDictionariesFromData.push(event.event));
    });
  }
  if (events) {
    events?.forEach(item => item.event && currentDictionariesFromData.push(item.event));
  }
  if (inspectors) {
    inspectors?.forEach(item => item.position && currentDictionariesFromData.push(item.position));
  }

  if (initialChecklists) {
    initialChecklists?.forEach(item => item.checkList && currentDictionariesFromData.push(item.checkList));
  }
  if (initialRequirements) {
    initialRequirements?.forEach(item => item.requirement && currentDictionariesFromData.push(item.requirement));
  }
  if (initialResultDecisions) {
    initialResultDecisions?.forEach(initialResultDecision => {
      if (initialResultDecision.titleSigner) {
        currentDictionariesFromData.push(initialResultDecision.titleSigner);
      }
      initialResultDecision.inspectors?.forEach(
        inspector => inspector.position && currentDictionariesFromData.push(inspector.position),
      );
    });
  }

  uniq(currentDictionariesFromData).forEach(item => {
    globalDictionaryTracker.addRequest(`${item.dictId}/${item.dictVersionId}`);
    resultArrayActions.push(getDictionariesItemValue(item));
  });
  accessPrivilege?.forEach(item => {
    globalDictionaryTracker.addRequest(`${DictionaryUUIDsEnum.KNO_ORGANIZATIONS}/${item.dictVersionId}`);
    resultArrayActions.push(
      getDictionariesItemValue({ dictId: DictionaryUUIDsEnum.KNO_ORGANIZATIONS, dictVersionId: item.dictVersionId }),
    );
  });
  yield all(resultArrayActions);
}

function* knmErknmSetValueByOptionsSaga(action: ActionType<typeof dictionariesItemValueGetSuccess>) {
  const { dictionaryUUID, dictionaryVersionUUID } = action.payload;
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);
  const initialFormValues = getKnmErknmInitialFormValues(state);
  const initialReasons = currentKnmData?.knmErknm?.reasons;
  const initialObjects = currentKnmData?.knmErknm?.objects;
  const kindControl = currentKnmData?.knmErknm?.kindControl;
  const kindKnm = currentKnmData?.knmErknm?.kindKnm;
  const initialEvents = currentKnmData?.knmErknm?.events;
  const initialInspectors = currentKnmData?.knmErknm?.inspectors;
  const settings = getAdminFetchSettings(state);

  switch (dictionaryUUID) {
    case DictionaryUUIDsEnum.KNO_ORGANIZATIONS: {
      const knmDataKnoOrganizationErknm = currentKnmData?.knmErknm?.knoOrganization;

      if (dictionaryVersionUUID === knmDataKnoOrganizationErknm?.dictVersionId) {
        const selectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
          state,
          dictionaryUUID,
          dictionaryVersionUUID,
        );

        yield put(
          knmErknmSetInitialFormValues({
            knoOrganizationErknm: selectOption && {
              ...selectOption,
              value: {
                ...selectOption.value,
                title: selectOption.title || undefined,
              },
              inspectionResult: currentKnmData?.knmErknm?.knoOrganization?.inspectionResult,
            },
          }),
        );
      }

      const initialCollaboratingOrganizations = currentKnmData?.knmErknm?.collaboratingOrganizations;

      if (initialCollaboratingOrganizations) {
        const isOn411 = isLaterThanAdminSetting(
          initialFormValues.created,
          settings,
          AdminSettingCodesEnum.COLLABORATION_KNO_ERVK_SEARCH_4_1_1,
        );

        yield put(
          knmErknmSetInitialFormValues({
            collaboratingOrganizationsErknm: initialCollaboratingOrganizations?.map(item => {
              const selectOption =
                isOn411 && item.organization?.dictId
                  ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                      state,
                      item.organization?.dictId,
                      item.organization?.dictVersionId,
                    )
                  : undefined;

              const formCollaboratingOrganization: IKnmErknmFormCollaboratingOrganization = {
                organization: selectOption,
                guid: item?.guid || undefined,
                dateCreate: item?.dateCreate || undefined,
                title: item?.title || undefined,
                ervkKnoOrganizationId: item?.ervkKnoOrganizationId || undefined,
                ervkTerritoryAgencyId: item?.ervkTerritoryAgencyId || undefined,
                ervkTerritoryAgencyVersionId: item?.ervkTerritoryAgencyVersionId || undefined,
                listId: item?.guid || uuidv4(),
              };

              return formCollaboratingOrganization;
            }),
          }),
        );
      }

      const initialAccessPrivilege = currentKnmData?.knmErknm?.accessPrivilege;

      if (currentKnmData?.knmErknm) {
        yield put(
          knmSetInitialAccessSettingsFormValues({
            accessPrivilege: convertKnmDataAccessPrivilegesToFormValues(initialAccessPrivilege, state),
          }),
        );
      }

      break;
    }
    case DictionaryUUIDsEnum.POSITIONS: {
      const selectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
        state,
        dictionaryUUID,
        dictionaryVersionUUID,
      );
      const titleSigner = currentKnmData?.knmErknm?.decision?.titleSigner;

      if (dictionaryVersionUUID === titleSigner?.dictVersionId) {
        yield put(
          knmErknmSetInitialFormValues({
            titleSigner: selectOption && {
              ...selectOption,
              inspectionResult: titleSigner?.inspectionResult,
            },
          }),
        );
      }

      // На демо произошел случай, по которому не заполнилась должность в предписании
      // Ниже обработка этого случая

      const initialOrganizations = currentKnmData?.knmErknm?.organizations;
      const { formResultDecisions } = yield getOrganizations(
        currentKnmData?.id,
        currentKnmData?.created,
        initialOrganizations,
        state,
      );

      yield put(
        knmErknmSetInitialFormValues({
          resultDecisions: formResultDecisions,
          agreements: convertAgreementsDataToFormValues(
            currentKnmData?.knmErknm?.agreements,
            state,
            formResultDecisions,
          ),
        }),
      );

      break;
    }
    case DictionaryUUIDsEnum.SUPERVISION_TYPE: {
      const isUseTransferringAuthorities = isLaterThanAdminSetting(
        initialFormValues.created,
        settings,
        AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
      );

      const selectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
        state,
        dictionaryUUID,
        dictionaryVersionUUID,
      );

      if (!kindControl || dictionaryVersionUUID === kindControl?.dictVersionId) {
        yield put(
          knmErknmSetInitialFormValues({
            kindControl: selectOption
              ? {
                  ...selectOption,
                  value: {
                    ...selectOption.value,
                    title: selectOption.title || undefined,
                  },
                  inspectionResult: kindControl?.inspectionResult,
                  label:
                    getKindControlSelectedLabel(selectOption, isUseTransferringAuthorities) ||
                    getKindControlLabel(selectOption, isUseTransferringAuthorities),
                  selectedLabel: getKindControlSelectedLabel(selectOption, isUseTransferringAuthorities),
                }
              : undefined,
          }),
        );
      }
      break;
    }
    case DictionaryUUIDsEnum.KNM_KIND: {
      const selectOption = getDictionarySelectOptionWithState<typeof dictionaryUUID>(
        state,
        dictionaryUUID,
        dictionaryVersionUUID,
      );

      if (dictionaryVersionUUID === kindKnm?.dictVersionId) {
        yield put(
          knmErknmSetInitialFormValues({
            kindKnm: selectOption && {
              ...selectOption,
              inspectionResult: kindKnm?.inspectionResult,
            },
          }),
        );
      }
      break;
    }
    case DictionaryUUIDsEnum.RISK_INDICATORY: {
      const isUseRiskIndicatorShortTitle = isLaterThanAdminSetting(
        initialFormValues.created,
        settings,
        AdminSettingCodesEnum.USE_RISK_INDICATOR_SHORT_TITLE_STARTS_FROM,
      );

      yield put(
        knmErknmSetInitialFormValues({
          reasonsErknm: initialReasons?.map((item, index) => {
            return {
              ...item,
              ...initialFormValues.reasonsErknm?.[index],
              riskIndicators:
                item.riskIndicators?.map(indicatorValue => {
                  const indicatorOption = indicatorValue.riskIndicator?.dictId
                    ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                        state,
                        indicatorValue.riskIndicator?.dictId,
                        indicatorValue.riskIndicator?.dictVersionId,
                      )
                    : undefined;

                  return {
                    guid: indicatorValue.guid,
                    riskIndicator:
                      isUseRiskIndicatorShortTitle && indicatorOption
                        ? { ...indicatorOption, label: indicatorOption.shortTitle || indicatorOption.title }
                        : indicatorOption,
                    indicatorRiskAuto: indicatorValue.indicatorRiskAuto || undefined,
                    indicatorRiskAttachment: indicatorValue.indicatorRiskAttachment || undefined,
                    isNew: false,
                  };
                }) || undefined,
              links: item.reason?.links || undefined,
              passportLinks: item.reason?.passportLinks || undefined,
              violationEvidences: item?.reason?.videoFixations
                ? item?.reason?.videoFixations?.map(fix => ({
                    guid: fix.guid || null,
                    date: convertInitialKnmDateToFormDate(fix.dateVideo, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
                    description: fix.descVideo,
                    link: fix.linkVideo,
                    originPassportId: fix.originPassportId,
                    listId: uuidv4(),
                    isNew: false,
                  }))
                : undefined,
              listId: item?.numGuid || uuidv4(),
            };
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.SUBJECT_TYPE: {
      yield put(
        knmErknmSetInitialFormValues({
          objectsErknm: initialObjects?.map((object, index) => {
            const selectOption = object.objectType?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.objectType?.dictId,
                  object.objectType?.dictVersionId,
                )
              : undefined;

            const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsErknm?.[index];
            const inspectionResults = object.inspectionResults || [];
            const parentInspectionResult = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.OBJECT_TYPE,
            );

            return {
              ...getInitialObject(initialObject),
              objectType: selectOption && {
                ...selectOption,
                inspectionResult: object.objectType?.inspectionResult || parentInspectionResult,
              },
            };
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.SUBJECT_KIND: {
      yield put(
        knmErknmSetInitialFormValues({
          objectsErknm: initialObjects?.map((object, index) => {
            const objectKindSelectOption = object.objectKind?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.objectKind?.dictId,
                  object.objectKind?.dictVersionId,
                )
              : undefined;
            const objectSubKindSelectOption = object.objectSubKind?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.objectSubKind?.dictId,
                  object.objectSubKind?.dictVersionId,
                )
              : undefined;
            const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsErknm?.[index];
            const inspectionResults = object.inspectionResults || [];
            const parentInspectionResultKind = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.OBJECT_KIND,
            );
            const parentInspectionResultSubKind = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.OBJECT_SUB_KIND,
            );

            return {
              ...getInitialObject(initialObject),
              tmpErvkCosId: object.tmpErvkCosId,
              objectKind: objectKindSelectOption && {
                ...objectKindSelectOption,
                inspectionResult: object.objectKind?.inspectionResult || parentInspectionResultKind,
              },
              objectSubKind: objectSubKindSelectOption && {
                ...objectSubKindSelectOption,
                inspectionResult: object.objectSubKind?.inspectionResult || parentInspectionResultSubKind,
              },
            };
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.SUBJECT_SUB_KIND: {
      yield put(
        knmErknmSetInitialFormValues({
          objectsErknm: initialObjects?.map((object, index) => {
            const objectKindSelectOption = object.objectKind?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.objectKind?.dictId,
                  object.objectKind?.dictVersionId,
                )
              : undefined;
            const objectSubKindSelectOption = object.objectSubKind?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.objectSubKind?.dictId,
                  object.objectSubKind?.dictVersionId,
                )
              : undefined;
            const objectTypeSelectOption = object.objectType?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.objectType?.dictId,
                  object.objectType?.dictVersionId,
                )
              : undefined;

            const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsErknm?.[index];
            const inspectionResults = object.inspectionResults || [];
            const parentInspectionResultKind = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.OBJECT_KIND,
            );
            const parentInspectionResultSubKind = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.OBJECT_SUB_KIND,
            );
            const parentInspectionResultType = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.OBJECT_TYPE,
            );

            return {
              ...getInitialObject(initialObject),
              tmpErvkCosId: object.tmpErvkCosId,
              objectKind: objectKindSelectOption && {
                ...objectKindSelectOption,
                inspectionResult: object.objectKind?.inspectionResult || parentInspectionResultKind,
              },
              objectSubKind: objectSubKindSelectOption && {
                ...objectSubKindSelectOption,
                inspectionResult: object.objectSubKind?.inspectionResult || parentInspectionResultSubKind,
              },
              objectType: objectTypeSelectOption && {
                ...objectTypeSelectOption,
                inspectionResult: object.objectType?.inspectionResult || parentInspectionResultType,
              },
            };
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.RISK_CATEGORY: {
      yield put(
        knmErknmSetInitialFormValues({
          objectsErknm: initialObjects?.map((object, index) => {
            const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsErknm?.[index];
            const inspectionResults = object.inspectionResults || [];
            const parentInspectionResultRisk = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.RISK_CATEGORY,
            );
            const parentInspectionResultDanger = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.DANGER_CLASS,
            );
            const riskCategorySelectOption = object.riskCategory?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.riskCategory?.dictId,
                  object.riskCategory?.dictVersionId,
                )
              : undefined;
            const dangerClassSelectOption = object.dangerClass?.dictId
              ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.DANGER_CLASS>(
                  state,
                  object.dangerClass?.dictId,
                  object.dangerClass?.dictVersionId,
                )
              : undefined;

            return {
              ...getInitialObject(initialObject),
              tmpErvkCosId: object.tmpErvkCosId,
              riskCategory: riskCategorySelectOption && {
                ...riskCategorySelectOption,
                inspectionResult: object.riskCategory?.inspectionResult || parentInspectionResultRisk,
              },
              dangerClass: dangerClassSelectOption && {
                ...dangerClassSelectOption,
                inspectionResult: object.dangerClass?.inspectionResult || parentInspectionResultDanger,
              },
            };
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.DANGER_CLASS: {
      yield put(
        knmErknmSetInitialFormValues({
          objectsErknm: initialObjects?.map((object, index) => {
            const selectOption = object.dangerClass?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.dangerClass?.dictId,
                  object.dangerClass?.dictVersionId,
                )
              : undefined;
            const initialObject: IKnmErknmFormObject | undefined = initialFormValues.objectsErknm?.[index];
            const inspectionResults = object.inspectionResults || [];
            const parentInspectionResultDanger = inspectionResults.find(
              i => i.inspectedField === InspectionResultFieldEnum.DANGER_CLASS,
            );

            return {
              ...getInitialObject(initialObject),
              tmpErvkCosId: object.tmpErvkCosId,
              dangerClass: selectOption && {
                ...selectOption,
                inspectionResult: object.dangerClass?.inspectionResult || parentInspectionResultDanger,
              },
            };
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.EVENT_TYPE: {
      const suspensionEvents: IKnmErknmFormSuspensionEvent[] = [];
      const eventsErknm = initialEvents?.map((item, index) => {
        const selectOption = item.event?.dictId
          ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
              state,
              item.event?.dictId,
              item.event?.dictVersionId,
            )
          : undefined;

        const formEvent: IKnmErknmFormEvent = {
          guid: undefined,
          startDate: undefined,
          stopDate: undefined,
          dateCreate: undefined,
          interactionCategory: undefined,
          inspectors: undefined,
          listId: item?.guid || uuidv4(),
          ...initialFormValues.eventsErknm?.[index],
          type: selectOption && {
            ...selectOption,
            inspectionResult: item.event?.inspectionResult,
          },
          isNew: item.isNew || false,
          submittedDocuments: createSubmittedDocuments(item.submittedDocuments),
        };

        suspensionEvents.push(...convertSuspensionEventsToFormData(item, formEvent, index, state));

        return formEvent;
      });

      sortSuspensionEvents(suspensionEvents);

      const initialOrganizations = currentKnmData?.knmErknm?.organizations;
      const { formActs, formOrganizations } = yield getOrganizations(
        currentKnmData?.id,
        currentKnmData?.created,
        initialOrganizations,
        state,
      );

      yield put(
        knmErknmSetInitialFormValues({
          eventsErknm,
          suspensionEvents,
          acts: formActs,
          organizations: formOrganizations || [],
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.PARTICIPANT_POSITIONS: {
      yield put(
        knmErknmSetInitialFormValues({
          inspectorsErknm: initialInspectors?.map(item => {
            const selectOption = item.position?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  item.position?.dictId,
                  item.position?.dictVersionId,
                )
              : undefined;
            const formInspector: IKnmErknmFormInspector = {
              ...item,
              position: selectOption && {
                ...selectOption,
                inspectionResult: item.position?.inspectionResult,
              },
              listId: item.guid || uuidv4(),
            };

            return formInspector;
          }),
        }),
      );

      const initialOrganizations = currentKnmData?.knmErknm?.organizations;
      const { formActs, formOrganizations, formResultDecisions } = yield getOrganizations(
        currentKnmData?.id,
        currentKnmData?.created,
        initialOrganizations,
        state,
      );
      const initialJudgments = currentKnmData?.knmErknm?.judgments;

      yield put(
        knmErknmSetInitialFormValues({
          organizations: formOrganizations || [],
          acts: formActs,
          resultDecisions: formResultDecisions,
          judgments: initialJudgments
            ? convertKnmDataJudgmentsToFormValues(state, initialJudgments, formActs, formResultDecisions)
            : undefined,
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.ACCEPTED_DOCUMENT: {
      const initialApproveDocs = currentKnmData?.knmErknm?.approveDocs;

      yield put(
        knmErknmSetInitialFormValues({
          approveDocs: initialApproveDocs?.map(item => {
            const selectOption = item.approveDoc?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  item.approveDoc?.dictId,
                  item.approveDoc?.dictVersionId,
                )
              : undefined;
            const formApproveDoc: IKnmErknmFormApproveDoc = {
              approveDoc: selectOption && {
                ...selectOption,
                inspectionResult: item.approveDoc?.inspectionResult,
              },
              dateCreate: item.dateCreate,
              numGuid: item.numGuid,
              listId: item.numGuid || uuidv4(),
            };

            return formApproveDoc;
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.CHECKLIST: {
      const initialChecklists = currentKnmData?.knmErknm?.checklists;

      yield put(
        knmErknmSetInitialFormValues({
          checklistsErknm: initialChecklists?.map(item => {
            const checklistSelectOption = item.checkList?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  item.checkList?.dictId,
                  item.checkList?.dictVersionId,
                )
              : undefined;

            let ervkCheckList: IDictionarySelectOption<IErvkCheckList> | undefined;

            if (item.ervkCheckList) {
              ervkCheckList = {
                ...item.ervkCheckList,
                id: item.ervkCheckList.id,
                label: `${item.ervkCheckList.number} - ${item.ervkCheckList.description}`,
                value: {
                  dictId: DictionaryUUIDsEnum.ERVK_CHECK_LIST,
                  dictVersionId: item.ervkCheckList.id || '',
                },
              };
            }

            return {
              guid: item.guid,
              ervkCheckList,
              checkList: checklistSelectOption,
              questions: checklistSelectOption?.questions?.map(question => {
                const questionAnswer = item.answers?.find(
                  checklistAnswer => checklistAnswer.codeQuestion === question.code,
                );

                return {
                  id: question.id,
                  code: question.code || '',
                  title: question.title || undefined,
                  answer: questionAnswer
                    ? {
                        codeQuestion: questionAnswer.codeQuestion,
                        answerType: questionAnswer.answerType
                          ? createChecklistAnswerTypeSelectOption(questionAnswer.answerType)
                          : undefined,
                        noteAnswer: questionAnswer.noteAnswer || undefined,
                        guid: questionAnswer.guid,
                        dateCreate: questionAnswer.dateCreate,
                      }
                    : undefined,
                  listId: question.code || uuidv4(),
                };
              }),
              questionCount: item.questionCount,
              uploadedViaSmev: item.uploadedViaSmev || undefined,
              dateCreate: item.dateCreate,
              listId: item.guid || uuidv4(),
              isNew: !!item.isNew,
            };
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.REQUIREMENT: {
      const initialRequirements = currentKnmData?.knmErknm?.requirements;
      const requirementsValue = initialRequirements?.map(item => {
        const requirementSelectOption = item.requirement?.dictId
          ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
              state,
              item.requirement?.dictId,
              item.requirement?.dictVersionId,
            )
          : undefined;

        return {
          isObserved: observedSelectOptions.find(
            observedSelectOption => observedSelectOption.value === item.isObserved,
          ),
          note: item.note || undefined,
          requirement: requirementSelectOption,
          manualTitle: requirementSelectOption?.title || undefined,
          manualNameNpa: requirementSelectOption?.nameNpa || undefined,
          manualDateNpa: convertInitialKnmDateToFormDate(
            requirementSelectOption?.numberNpa,
            DATE_FORMAT_LONG_FNS,
            SYSTEM_DATE_FORMAT_FNS,
          ),
          guid: item.guid,
          dateCreate: item.dateCreate,
          listId: item.guid || uuidv4(),
        };
      });

      yield put(
        knmErknmSetInitialFormValues({
          requirementsErknm: requirementsValue,
          violatedRequirements: requirementsValue?.filter(requirement => requirement.isObserved?.value === false),
        }),
      );
      break;
    }
    default:
  }
}

function* knmErknmInitFormValuesSaga(action: ActionType<typeof knmGetSuccess>) {
  const { data: currentKnmData } = action.payload;
  const initialType = currentKnmData?.type;
  const initialObjects = currentKnmData?.knmErknm?.objects;
  const state: IRootState = yield select();
  const initialFormValues = getKnmErknmInitialFormValues(state);
  const initialDocuments = currentKnmData?.documents;
  const webDocuments = initialDocuments
    ?.filter(item => item.type?.code === DocumentsTypeCodesEnum.WEB_0)
    .reduce((acc: IKnmAttachment[] | null, item) => {
      let newAcc = acc ? [...acc] : null;

      if (item.attachments?.length) {
        if (!acc) {
          newAcc = [];
        }
        item.attachments.forEach(attachment => {
          newAcc?.push(attachment);
        });

        return newAcc;
      }

      return null;
    }, null);
  const initialChangePlanReason = currentKnmData?.knmErknm?.changePlanReason;
  const knmPlanReasonChangeOptions = geCatalogsKnmPlanReasonChangeTypesSelectOptions(state);
  const isInitialApproveAppeal = currentKnmData.knmErknm?.approve?.appeal;
  const initialRejectReasonType = currentKnmData.knmErknm?.approve?.rejectReasonType;
  const isInitialIsHasCollaboratingOrganization = currentKnmData.knmErknm?.isHasCollaboratingOrganization;
  const isInitialIsApprovedOnPaper = currentKnmData.knmErknm?.isApprovedOnPaper;
  const isInitialIsChecklistsUsed = currentKnmData.knmErknm?.isChecklistsUsed;
  const isInitialIsAppealed = currentKnmData?.knmErknm?.isAppealed;
  const isPlanType = currentKnmData?.type?.code === KnmTypeCodesEnum.PP;
  const initialReasonDocuments = currentKnmData?.knmErknm?.reasonDocuments;
  const initialApprovedOnPaperDocuments = currentKnmData?.knmErknm?.approvedOnPaperDocuments;
  const initialExperts = currentKnmData?.knmErknm?.experts;
  const initialServiceDocument = currentKnmData?.knmErknm?.serviceDocument;
  const initialOrganizations = currentKnmData?.knmErknm?.organizations;
  const initialAgreements = currentKnmData?.knmErknm?.agreements;
  const { formActs, formOrganizations, formResultDecisions } = yield getOrganizations(
    currentKnmData?.id,
    currentKnmData?.created,
    initialOrganizations,
    state,
  );
  const initialJudgments = currentKnmData?.knmErknm?.judgments;
  const initialReasons = currentKnmData?.knmErknm?.reasons;
  const initialEvents = currentKnmData?.knmErknm?.events;
  const knmDataKnoOrganizationErknm = currentKnmData?.knmErknm?.knoOrganization;
  let settings = getAdminFetchSettings(state);

  const isShow22 = isLaterThanAdminSetting(
    currentKnmData.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM,
  );
  const isUseTransferringAuthorities = isLaterThanAdminSetting(
    initialFormValues.created,
    settings,
    AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
  );
  const isOn411 = isLaterThanAdminSetting(
    initialFormValues.created,
    settings,
    AdminSettingCodesEnum.COLLABORATION_KNO_ERVK_SEARCH_4_1_1,
  );
  const knoOrganizationErknm = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNO_ORGANIZATIONS>(
    state,
    knmDataKnoOrganizationErknm?.dictId,
    knmDataKnoOrganizationErknm?.dictVersionId,
  );
  const initialCollaboratingOrganizations = currentKnmData?.knmErknm?.collaboratingOrganizations;
  const titleSigner = currentKnmData?.knmErknm?.decision?.titleSigner;
  const titleSignerSelectOption = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.POSITIONS>(
    state,
    titleSigner?.dictId,
    titleSigner?.dictVersionId,
  );
  const kindControl = currentKnmData?.knmErknm?.kindControl;
  const kindControlSelectOption = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUPERVISION_TYPE>(
    state,
    kindControl?.dictId,
    kindControl?.dictVersionId,
  );
  const kindKnm = currentKnmData?.knmErknm?.kindKnm;
  const kindKnmSelectOption = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNM_KIND>(
    state,
    kindKnm?.dictId,
    kindKnm?.dictVersionId,
  );
  let ervkDictionary: IDictionaryVersion | undefined;
  let ervkValue: IKnmErvk | undefined;

  if (initialReasons && initialReasons.length && initialReasons[0].reason && initialReasons[0].reason.ervkUuid) {
    ervkDictionary = yield fetchDictionaryValue(DictionaryUUIDsEnum.ERVK, initialReasons[0].reason.ervkUuid);
  }

  let isPassportByCheckPrescriptionFailed = false;
  let isPassportByCheckPrescriptionOldRules = false;

  // на холодном старте (очистка кэша) настройки могут не успеть загрузиться.
  // state захвачен в начале саги, поэтому settings из него пусты — дозагружаем и ждём.
  if (!settings?.length) {
    yield put(adminGetFetchSettings());
    yield race({
      success: take(AdminActionsEnum.ADMIN_GET_FETCH_SETTINGS_SUCCESS),
      timeout: delay(5000),
    });
    settings = yield select(getAdminFetchSettings);
  }

  if (
    initialReasons?.[0]?.reason?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS &&
    !!initialReasons?.[0]?.reason?.links?.[0]?.id
  ) {
    const selectedLinkKnmData: IKnm = yield fetchKnm(initialReasons[0].reason.links[0].id);

    const isEnablePrescriptionCheck = isLaterThanAdminSetting(
      selectedLinkKnmData.created,
      settings,
      AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
    );

    if (
      isEnablePrescriptionCheck &&
      selectedLinkKnmData.knmErknm?.reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK
    ) {
      try {
        const grandSourceId = selectedLinkKnmData.knmErknm?.reasons?.[0]?.reason?.links?.[0]?.id;
        let isGrandSourceOldRules = false;

        if (grandSourceId) {
          const grandSource: IKnm = yield fetchKnm(grandSourceId);

          isGrandSourceOldRules = isSourceCreatedByOldRules(grandSource.created, settings);
        }

        isPassportByCheckPrescriptionFailed = !isGrandSourceOldRules;
      } catch (e) {
        isPassportByCheckPrescriptionFailed = false;
      }
    }
  }

  if (
    initialReasons?.[0]?.reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK &&
    !!initialReasons?.[0]?.reason?.links?.[0]?.id
  ) {
    try {
      const sourceKnmData: IKnm = yield fetchKnm(initialReasons[0].reason.links[0].id);

      isPassportByCheckPrescriptionOldRules = isSourceCreatedByOldRules(sourceKnmData.created, settings);
    } catch (e) {
      isPassportByCheckPrescriptionOldRules = false;
    }
  }

  yield put(knmErknmSetPrescriptionOldRules(isPassportByCheckPrescriptionOldRules));
  yield put(knmErknmSetPrescriptionFailed(isPassportByCheckPrescriptionFailed));

  if (ervkDictionary) {
    ervkValue = JSON.parse(ervkDictionary.value);
  }

  if (typeof currentKnmData.planId === 'number') {
    yield put(knmPlanFindGet(currentKnmData.planId));
  }

  const initialInspectors = currentKnmData?.knmErknm?.inspectors;
  const initialApproveDocs = currentKnmData?.knmErknm?.approveDocs;
  const initialDocTarget = currentKnmData?.knmErknm?.docTarget;
  const initialChecklists = currentKnmData?.knmErknm?.checklists;
  const initialRequirements = currentKnmData?.knmErknm?.requirements;
  const requirementsValue = initialRequirements?.map(item => {
    const requirementSelectOption = item.requirement?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.REQUIREMENT>(
          state,
          item.requirement?.dictId,
          item.requirement?.dictVersionId,
        )
      : undefined;

    return {
      isObserved: observedSelectOptions.find(observedSelectOption => observedSelectOption.value === item.isObserved),
      note: item.note || undefined,
      requirement: requirementSelectOption,
      manualTitle: requirementSelectOption?.title || undefined,
      manualNameNpa: requirementSelectOption?.nameNpa || undefined,
      manualDateNpa: convertInitialKnmDateToFormDate(
        requirementSelectOption?.numberNpa,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      guid: item.guid,
      dateCreate: item.dateCreate,
      listId: item.guid || uuidv4(),
    };
  });

  const initialRequirementsNpa = currentKnmData?.knmErknm?.requirementsNew;

  const requirementsNpaValue = initialRequirementsNpa?.map<IKnmErknmFormRequirementNpa>(item => ({
    guid: item.guid,
    npa: {
      id: item.npaId,
      number: item.number || undefined,
      acceptanceDate: formatDate({
        date: item.acceptanceDate ?? '',
        dateFormat: DATE_FORMAT_LONG_FNS,
        parseFormat: SYSTEM_DATE_FORMAT_FNS,
      }),
      title: item.title || undefined,
    },
    structuralUnit: {
      id: item.structuralUnit.id,
      title: item.structuralUnit.title || undefined,
    },
    mandatory: item.structuralUnit.mandatoryRequirements
      ? item.structuralUnit.mandatoryRequirements.map(mandatory => ({
          mandatory: {
            id: mandatory.id,
            title: mandatory.title || '',
          },
          category: mandatory.category ? createMandatoryCategorySelectOption(mandatory.category) : undefined,
          violations: mandatory.violations?.length ? convertKnmDataViolationsToFormValues(mandatory.violations) : null,
        }))
      : [],
    inspectionResults: item.inspectionResults,
    inspectionResult: item.inspectionResult,
    isFilledFromLink: item.isFilledFromLink || undefined,
  }));

  const events = initialEvents?.map((item, index) => {
    const selectOption = item.event?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
          state,
          item.event?.dictId,
          item.event?.dictVersionId,
        )
      : undefined;
    const formEvent: IKnmErknmFormEvent = {
      type: selectOption && {
        ...selectOption,
        inspectionResult: item.event?.inspectionResult,
      },
      ...(!selectOption && initialFormValues.eventsErknm?.[index]),
      guid: item.guid,
      startDate: convertInitialKnmDateToFormDate(item.startDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      stopDate: convertInitialKnmDateToFormDate(item.stopDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      dateCreate: item.dateCreate,
      listId: item?.guid || uuidv4(),
      interactionCategory: item?.interactionCategory
        ? createInteractionCategorySelectOption(item.interactionCategory)
        : undefined,
      inspectors: item.inspectors
        ? item.inspectors.map(x => {
            const eventInspectorForm: IKnmErknmFormEventInspector = {
              guid: x.guid,
              fullName: createKnmEventInspectorSelectOption(x.fullName, x.guid),
            };

            return eventInspectorForm;
          })
        : undefined,
      isNew: !!item.isNew,
      submittedDocuments: createSubmittedDocuments(item.submittedDocuments),
      originalStopDate: convertInitialKnmDateToFormDate(
        item.originalStopDate,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      isFilledFromLink: item.isFilledFromLink || undefined,
    };

    return formEvent;
  });
  const suspensionEvents: IKnmErknmFormSuspensionEvent[] = [];

  initialEvents?.forEach((event, index) => {
    suspensionEvents.push(...convertSuspensionEventsToFormData(event, events?.[index], index, state));
  });

  sortSuspensionEvents(suspensionEvents);
  const initialAccessPrivilege = currentKnmData?.knmErknm?.accessPrivilege;

  if (currentKnmData?.knmErknm) {
    yield put(
      knmSetInitialAccessSettingsFormValues({
        accessPrivilege: convertKnmDataAccessPrivilegesToFormValues(initialAccessPrivilege, state),
      }),
    );
  }
  const initialNotifications = currentKnmData?.subjectNotifications;

  // Проверяем поле knmErknm.reasons[0].reason.ervkUuid, чтобы загружать данные по IT-аккредитации или нет
  let reasonRequirementsValue: IReasonRequirements | undefined;

  if (initialReasons && initialReasons.length > 0 && initialReasons[0].reason?.ervkUuid) {
    const dictionaryValue: IDictionaryVersion | undefined = yield fetchDictionaryValue(
      DictionaryUUIDsEnum.ERVK,
      initialReasons[0].reason?.ervkUuid,
    );

    // Значения ЕРВК может не оказаться в справочниках (get-dictionary-value отдаёт 200 с пустым телом) -
    // в этом случае пропускаем проверку IT-аккредитации, а не роняем инициализацию формы.
    if (dictionaryValue) {
      reasonRequirementsValue = parseDictionaryVersionValue<IReasonRequirements>(dictionaryValue);
    }

    if (reasonRequirementsValue && reasonRequirementsValue.it !== null) {
      const hasItOrganization = !!initialOrganizations?.find(i => i.isPresentInItRegistry);
      const hasNotItOrganization = !!initialOrganizations?.find(
        i => i.isPresentInItRegistry !== null && !i.isPresentInItRegistry,
      );

      if (
        (reasonRequirementsValue?.it === true && hasNotItOrganization) ||
        (reasonRequirementsValue?.it === false && hasItOrganization)
      ) {
        yield put(
          addNoticeAction({
            renderContent: CHECKING_IT_ACCREDITATION,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHECKING_IT_ACCREDITATION,
          }),
        );
      }
    }
  }

  const convertTime = (date: string, time: string) => {
    const dateParts = date.split('-');
    const timeParts = time.split(':');

    const newDate = new Date();

    newDate.setFullYear(Number(dateParts[0]));
    newDate.setMonth(Number(dateParts[1]) - 1);
    newDate.setDate(Number(dateParts[2]));

    newDate.setHours(Number(timeParts[0]));
    newDate.setMinutes(Number(timeParts[1]));
    newDate.setSeconds(Number(timeParts[2]));

    return newDate;
  };

  let startTime: string | undefined;

  if (currentKnmData.startTime && currentKnmData.startDate) {
    startTime = convertTime(currentKnmData.startDate, currentKnmData.startTime).toString();
  }

  let stopTime: string | undefined;

  if (currentKnmData.stopTime && currentKnmData.stopDate) {
    stopTime = convertTime(currentKnmData.stopDate, currentKnmData.stopTime).toString();
  }

  const knmRemarkSelectOption: IKnmRemarkSelectOption[] = yield select(getCatalogKnmRemarkSelectOptions);
  const initialApproveRemarks = currentKnmData?.knmErknm?.approve?.remarks;

  const knmTypesCanceled: ICatalog<IKnmAbrogationReason[]> = yield select(getKnmTypesCanceled);

  const isKnmStatusApproving = currentKnmData.status?.code === KnmErknmStatusCodesEnum.APPROVING;
  const planStatus = getPlanListPlanErknmStatus(state);
  const isProsecutorOrSuperProsecutor = getUserRoleIsProsecutorOrSuperProsecutor(state);

  const isValidateChangePlanReason =
    isProsecutorOrSuperProsecutor &&
    planStatus === ErknmPlanStatusesEnum.APPROVED &&
    isKnmStatusApproving &&
    initialChangePlanReason?.some(
      item =>
        item?.reasonChangeType?.code === 'RSN_CHNG_PP_XII' ||
        item?.reasonChangeType?.code === 'RSN_CHNG_PP_XXI' ||
        item?.reasonChangeType?.code === 'RSN_CHNG_PP_XX',
    );

  let approvedPlanTypeSelectOption = planFullListTypeApprovedSelectOptions.find(
    item => item.value === currentKnmData.knmErknm?.approve?.approved,
  );

  if (isShow22 && isValidateChangePlanReason) {
    approvedPlanTypeSelectOption = planFullListTypeApprovedSelectOptions.find(
      item => item.value === currentKnmData.knmErknm?.approve?.approved,
    );
  } else if (currentKnmData.knmErknm?.approve?.wasRejected === true) {
    approvedPlanTypeSelectOption = planTypeRejectAppealedSelectOptions.find(
      item => item.value === currentKnmData.knmErknm?.approve?.approved,
    );
  } else {
    approvedPlanTypeSelectOption = planFullListTypeApprovedSelectRenamedOptions.find(
      item => item.value === currentKnmData.knmErknm?.approve?.approved,
    );
  }

  const abrogationReasonId = parseInt(currentKnmData?.knmErknm?.abrogation?.reasonId || '0', 10);
  let abrogationReasonIdOption;

  if (knmTypesCanceled?.data?.length) {
    const item: IKnmAbrogationReason | undefined = knmTypesCanceled.data.find(i => i.id === abrogationReasonId);

    if (item) {
      abrogationReasonIdOption = { ...item, label: item.name, value: item.code };
    }
  }
  yield put(
    knmErknmSetInitialFormValues({
      isTest: currentKnmData.isTest || undefined,
      draft: currentKnmData.draft || undefined,
      draftSavedAt: currentKnmData.draftSavedAt || undefined,
      ervkValue,
      knoOrganizationErknm: knoOrganizationErknm && {
        ...knoOrganizationErknm,
        value: {
          ...knoOrganizationErknm.value,
          title: knoOrganizationErknm.title || undefined,
        },
        inspectionResult: knmDataKnoOrganizationErknm?.inspectionResult,
      },
      notNotifySubject: currentKnmData?.knmErknm?.notNotifySubject || undefined,
      completedRO: currentKnmData?.knmErknm?.completedRO || undefined,
      extractVersion: currentKnmData?.knmErknm?.extractVersion || undefined,
      extractPerform: !currentKnmData?.knmErknm?.extractPerform
        ? undefined
        : {
            active: currentKnmData?.knmErknm?.extractPerform.active || undefined,
            attachments: currentKnmData?.knmErknm?.extractPerform.attachments || undefined,
            correlationToken: currentKnmData?.knmErknm?.extractPerform.correlationToken || undefined,
            description: currentKnmData?.knmErknm?.extractPerform.description || undefined,
            guid: currentKnmData?.knmErknm?.extractPerform.guid || undefined,
            type: currentKnmData?.knmErknm?.extractPerform.type || undefined,
          },
      abrogation: !currentKnmData?.knmErknm?.abrogation
        ? null
        : {
            comment: currentKnmData?.knmErknm?.abrogation.comment,
            reasonId: abrogationReasonIdOption,
            reasonFile: currentKnmData?.knmErknm?.abrogation.reasonFile
              ? [currentKnmData?.knmErknm?.abrogation.reasonFile]
              : undefined,
            link: currentKnmData?.knmErknm?.abrogation.link,
          },
      rescheduling: !currentKnmData?.knmErknm?.rescheduling
        ? null
        : currentKnmData?.knmErknm?.rescheduling.map(reschedule => {
            return {
              isNew: !!reschedule.isNew,
              guid: reschedule.guid || undefined,
              newStartDate: convertInitialKnmDateToFormDate(reschedule.newStartDate, DATE_FORMAT_LONG_FNS),
              newEndDate: convertInitialKnmDateToFormDate(reschedule.newEndDate, DATE_FORMAT_LONG_FNS),
              newDirectDurationDays: reschedule.newDirectDurationDays || undefined,
              newDurationDays: reschedule.newDurationDays || undefined,
              newDurationHours: reschedule.newDurationHours || undefined,
              comment: reschedule.comment || undefined,
              oldStartDate: convertInitialKnmDateToFormDate(reschedule.oldStartDate, DATE_FORMAT_LONG_FNS),
              oldEndDate: convertInitialKnmDateToFormDate(reschedule.oldEndDate, DATE_FORMAT_LONG_FNS),
              initiationDate: convertInitialKnmDateToFormDate(reschedule.initiationDate, DATE_FORMAT_LONG_FNS),
            };
          }),
      kindKnm: kindKnmSelectOption && {
        ...kindKnmSelectOption,
        inspectionResult: kindKnm?.inspectionResult,
      },
      kindControl: kindControlSelectOption
        ? {
            ...kindControlSelectOption,
            value: {
              ...kindControlSelectOption.value,
              title: kindControlSelectOption.title || undefined,
            },
            inspectionResult: kindControl?.inspectionResult,
            label:
              getKindControlSelectedLabel(kindControlSelectOption, isUseTransferringAuthorities) ||
              getKindControlLabel(kindControlSelectOption, isUseTransferringAuthorities),
            selectedLabel: getKindControlSelectedLabel(kindControlSelectOption, isUseTransferringAuthorities),
          }
        : undefined,
      titleSigner: titleSignerSelectOption && {
        ...titleSignerSelectOption,
        inspectionResult: titleSigner?.inspectionResult,
      },
      reasonRequirements: reasonRequirementsValue,
      approveDocs: initialApproveDocs?.map(item => {
        const selectOption = item.approveDoc?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.ACCEPTED_DOCUMENT>(
              state,
              item.approveDoc?.dictId,
              item.approveDoc?.dictVersionId,
            )
          : undefined;
        const formApproveDoc: IKnmErknmFormApproveDoc = {
          approveDoc: selectOption && {
            ...selectOption,
            inspectionResult: item.approveDoc?.inspectionResult,
          },
          dateCreate: item.dateCreate,
          numGuid: item.numGuid,
          listId: item.numGuid || uuidv4(),
        };

        return formApproveDoc;
      }),
      docTarget: initialDocTarget?.map(doc => {
        const selectOption: IDictionaryPermitDocSelectOption | undefined = !doc.ervkControlKindDocTarget?.targetTitle
          ? undefined
          : {
              type: doc.ervkControlKindDocTarget.type,
              targetId: doc.ervkControlKindDocTarget.targetId,
              targetTitle: doc.ervkControlKindDocTarget.targetTitle,
              id: doc.ervkControlKindDocTarget?.targetId,
              label: doc.ervkControlKindDocTarget?.targetTitle,
              value: {
                dictId: DictionaryUUIDsEnum.PERMIT_DOCS,
                dictVersionId: '',
              },
            };

        return {
          ervkControlKindDocTarget: selectOption,
          specificRequirementAndClause: doc.specificRequirementAndClause || undefined,
          file: doc.file ? [doc.file] : undefined,
          listId: uuidv4(),
          docTargetType: getDocTypeOption(doc.ervkControlKindDocTarget?.type || ''),
          guid: doc.guid,
          inspectionResult: doc.inspectionResult || undefined,
        };
      }),
      requirementsErknm: requirementsValue,
      violatedRequirements: requirementsValue?.filter(requirement => requirement.isObserved?.value === false),
      checklistsErknm: initialChecklists?.map(item => {
        const checklistSelectOption = item.checkList?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.CHECKLIST>(
              state,
              item.checkList?.dictId,
              item.checkList?.dictVersionId,
            )
          : undefined;

        let ervkCheckList: IDictionarySelectOption<IErvkCheckList> | undefined;

        if (item.ervkCheckList) {
          ervkCheckList = {
            ...item.ervkCheckList,
            id: item.ervkCheckList.id,
            label: `${item.ervkCheckList.number} - ${item.ervkCheckList.description}`,
            value: {
              dictId: DictionaryUUIDsEnum.ERVK_CHECK_LIST,
              dictVersionId: item.ervkCheckList.id || '',
            },
          };
        }

        return {
          guid: item.guid,
          ervkCheckList,
          checkList: checklistSelectOption,
          questions: checklistSelectOption?.questions?.map(question => {
            const questionAnswer = item.answers?.find(
              checklistAnswer => checklistAnswer.codeQuestion === question.code,
            );

            return {
              id: question.id,
              code: question.code || '',
              title: question.title || undefined,
              answer: questionAnswer
                ? {
                    codeQuestion: questionAnswer.codeQuestion,
                    answerType: questionAnswer.answerType
                      ? createChecklistAnswerTypeSelectOption(questionAnswer.answerType)
                      : undefined,
                    noteAnswer: questionAnswer.noteAnswer || undefined,
                    guid: questionAnswer.guid,
                    dateCreate: questionAnswer.dateCreate,
                  }
                : undefined,
              listId: question.code || uuidv4(),
            };
          }),
          questionCount: item.questionCount,
          dateCreate: item.dateCreate,
          listId: item.guid || uuidv4(),
          uploadedViaSmev: item.uploadedViaSmev || undefined,
          isNew: !!item.isNew,
        };
      }),
      inspectorsErknm: initialInspectors?.map(item => {
        const selectOption = item.position?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
              state,
              item.position?.dictId,
              item.position?.dictVersionId,
            )
          : undefined;

        const formInspector: IKnmErknmFormInspector = {
          ...item,
          position: selectOption && {
            ...selectOption,
            inspectionResult: item.position?.inspectionResult,
          },
          listId: item.guid || uuidv4(),
        };

        return formInspector;
      }),
      collaboratingOrganizationsErknm: initialCollaboratingOrganizations?.map(item => {
        const selectOption =
          isOn411 && item.organization?.dictId
            ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNO_ORGANIZATIONS>(
                state,
                item.organization?.dictId,
                item.organization?.dictVersionId,
              )
            : undefined;

        const formCollaboratingOrganization: IKnmErknmFormCollaboratingOrganization = {
          organization: selectOption,
          guid: item?.guid || undefined,
          dateCreate: item?.dateCreate || undefined,
          title: item.title || undefined,
          ervkKnoOrganizationId: item?.ervkKnoOrganizationId || undefined,
          ervkTerritoryAgencyId: item?.ervkTerritoryAgencyId || undefined,
          ervkTerritoryAgencyVersionId: item?.ervkTerritoryAgencyVersionId || undefined,
          listId: item?.guid || uuidv4(),
        };

        return formCollaboratingOrganization;
      }),
      typeErknm: initialType ? createKnmTypeSelectOption(initialType) : undefined,

      objectsErknm: initialObjects?.map(object => {
        const dangerClassSelectOption = object.dangerClass?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
              state,
              object.dangerClass?.dictId,
              object.dangerClass?.dictVersionId,
            )
          : undefined;
        const objectKindSelectOption = object.objectKind?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
              state,
              object.objectKind?.dictId,
              object.objectKind?.dictVersionId,
            )
          : undefined;
        const objectSubKindSelectOption = object.objectSubKind?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_SUB_KIND>(
              state,
              object.objectSubKind?.dictId,
              object.objectSubKind?.dictVersionId,
            )
          : undefined;
        const objectTypeSelectOption = object.objectType?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_TYPE>(
              state,
              object.objectType?.dictId,
              object.objectType?.dictVersionId,
            )
          : undefined;
        const riskCategorySelectOption = object.riskCategory?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_CATEGORY>(
              state,
              object.riskCategory?.dictId,
              object.riskCategory?.dictVersionId,
            )
          : undefined;

        let objRegion: IKnmDistrictSelectOption | undefined;
        let objFiasRegion: IKnmDistrictSelectOption | undefined;

        const list = getDistrictSelectOptions(state);

        // Смотрим Место проведения профилактического мероприятия
        if (object?.region) {
          if (typeof object.region === 'object') {
            objRegion = list.find(i => i.id.toString() === (object.region as IFederalDistrict)?.id?.toString());
          } else {
            objRegion = list.find(i => i.id.toString() === object?.region);
          }
        }
        if (object?.fiasRegion) {
          if (typeof object.fiasRegion === 'object') {
            objFiasRegion = list.find(i => i.id.toString() === (object.fiasRegion as IFederalDistrict)?.id?.toString());
          } else {
            objFiasRegion = list.find(i => i.id.toString() === object?.fiasRegion);
          }
        }

        const inspectionResults = object.inspectionResults || [];
        const parentInspectionResultKind = inspectionResults.find(
          i => i.inspectedField === InspectionResultFieldEnum.OBJECT_KIND,
        );
        const parentInspectionResultSubKind = inspectionResults.find(
          i => i.inspectedField === InspectionResultFieldEnum.OBJECT_SUB_KIND,
        );
        const parentInspectionResultType = inspectionResults.find(
          i => i.inspectedField === InspectionResultFieldEnum.OBJECT_TYPE,
        );
        const parentInspectionResultDanger = inspectionResults.find(
          i => i.inspectedField === InspectionResultFieldEnum.DANGER_CLASS,
        );
        const parentInspectionResultRisk = inspectionResults.find(
          i => i.inspectedField === InspectionResultFieldEnum.RISK_CATEGORY,
        );

        return {
          dangerClass: dangerClassSelectOption
            ? {
                ...dangerClassSelectOption,
                inspectionResult: object.dangerClass?.inspectionResult || parentInspectionResultDanger,
              }
            : undefined,
          objectKind: objectKindSelectOption
            ? {
                ...objectKindSelectOption,
                inspectionResult: object.objectKind?.inspectionResult || parentInspectionResultKind,
              }
            : undefined,
          objectSubKind: objectSubKindSelectOption
            ? {
                ...objectSubKindSelectOption,
                inspectionResult: object.objectSubKind?.inspectionResult || parentInspectionResultSubKind,
              }
            : undefined,
          objectType: objectTypeSelectOption
            ? {
                ...objectTypeSelectOption,
                inspectionResult: object.objectType?.inspectionResult || parentInspectionResultType,
              }
            : undefined,
          riskCategory: riskCategorySelectOption
            ? {
                ...riskCategorySelectOption,
                inspectionResult: object.riskCategory?.inspectionResult || parentInspectionResultRisk,
              }
            : undefined,
          address: object.address || undefined,
          guid: object.guid || undefined,
          ervkNumber: object.ervkNumber || undefined,
          ervkId: object.ervkId || undefined,
          tmpErvkCosId: object.tmpErvkCosId || undefined,
          cosId: object.cosId || undefined,
          cosVersion: object.cosVersion || undefined,
          name: object.name || undefined,
          streetGuid: object.streetGuid || undefined,
          houseGuid: object.houseGuid || undefined,
          roomGuid: object.roomGuid || undefined,
          steadGuid: object.steadGuid || undefined,
          addressFns: object.addressFns || undefined,
          autocomplete: typeof object.autocomplete === 'boolean' ? object.autocomplete : undefined,
          dateCreate: object.dateCreate || undefined,
          geolocation: object.geolocation || undefined,
          listId: object.guid || uuidv4(),
          isNew: !!object.isNew,
          manualAddress: object.manualAddress || undefined,
          notInFIAS: object.notInFIAS || undefined,
          region: objRegion,
          fiasRegion: objFiasRegion,
          regionName: object.regionName || undefined,
          isErrorFiasValue: undefined,
          inspectionResults: object.inspectionResults || undefined,
          organizationErvkId: object.organizationErvkId || undefined,
          updating: object.updating || undefined,
          isFilledFromLink: object.isFilledFromLink || undefined,
        };
      }),
      documents: initialDocuments?.map(document => {
        return {
          ...document,
          descriptionReactQuillEditor: handleReactQuillDeltaValue(
            document.descriptionReactQuillEditor || document.description,
          ),
          type: document?.type ? createKnmDocumentTypeSelectOption(document?.type) : undefined,
          attachments: document.attachments || undefined,
          listId: document.guid || uuidv4(),
        };
      }),
      webDocuments,
      changePlanReason: initialChangePlanReason?.map(item => {
        const formOrganizationDocument: IKnmErknmFormChangePlanReason = {
          code: item.code || undefined,
          reasonChangeType: knmPlanReasonChangeOptions.find(option => option.code === item.reasonChangeType?.code),
          guid: item.guid || undefined,
          dateCreate: item.dateCreate || undefined,
          listId: item.guid || uuidv4(),
          isNew: false,
          approveChangePlanDateDecision: item.approve?.dateDecision,
          approveChangePlanNumberDecision: item.approve?.numberDecision,
          approveChangePlanFioSigner: item.approve?.fioSigner,
          approveChangePlanTitleSigner: item.approve?.titleSigner,
          approveChangePlanFile: item.approve?.approveFile,
          approvedChangePlanErknm: item.approve?.approved
            ? approvedChangeKnmSelectOptions.find(option => option.id === item.approve?.approved)
            : undefined,
        };

        return formOrganizationDocument;
      }),
      statusCode: currentKnmData.status?.code,
      planId: typeof currentKnmData.planId === 'number' ? currentKnmData.planId.toString() : undefined,
      planGuid: currentKnmData.planGuid || undefined,
      isCreatePlan: currentKnmData.createPlan || undefined,
      planSelectOption:
        typeof currentKnmData.planId === 'number' ? createPlanSelectOption(currentKnmData.planId) : undefined,
      dateTimeDecision: convertInitialKnmDateToFormDate(
        currentKnmData.knmErknm?.decision?.dateTimeDecision,
        DATE_FORMAT_LONG_MINUTE_FNS,
        DATE_FORMAT_ISO_FNS,
      ),
      numberDecision: currentKnmData.knmErknm?.decision?.numberDecision || undefined,
      placeDecision: currentKnmData.knmErknm?.decision?.placeDecision || undefined,
      fioSigner: currentKnmData.knmErknm?.decision?.fioSigner || undefined,
      notInFIAS: currentKnmData.knmErknm?.decision?.notInFIAS || undefined,
      manualAddress: currentKnmData.knmErknm?.decision?.manualAddress || undefined,
      region: undefined,
      fiasRegion: undefined,
      decisionDisabled:
        typeof currentKnmData.knmErknm?.decision?.decisionDisabled === 'boolean'
          ? currentKnmData.knmErknm?.decision?.decisionDisabled
          : undefined,
      disabledDecisionMessage: currentKnmData.knmErknm?.decision?.disabledDecisionMessage || undefined,
      startTime,
      startDate: convertInitialKnmDateToFormDate(
        currentKnmData.startDate,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      stopTime,
      stopDate: convertInitialKnmDateToFormDate(currentKnmData.stopDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      places: currentKnmData.knmErknm?.places?.map(item => {
        const formPlace: IKnmErknmFormPlace = {
          value: item.value || undefined,
          guid: item.guid,
          dateCreate: item.dateCreate,
          listId: item.guid || uuidv4(),
          manualAddress: item.manualAddress || undefined,
          notInFIAS: item.notInFIAS || undefined,
          region: undefined,
          fiasRegion: undefined,
          regionName: item.regionName || undefined,
          matchesObjectAddress: item.matchesObjectAddress || undefined,
        };

        return formPlace;
      }),
      organizationDocuments: currentKnmData.knmErknm?.organizationDocuments?.map(item => {
        const formOrganizationDocument: IKnmErknmFormOrganizationDocument = {
          value: item.value || undefined,
          guid: item.guid,
          dateCreate: item.dateCreate,
          listId: item.guid || uuidv4(),
        };

        return formOrganizationDocument;
      }),
      durationDays:
        typeof currentKnmData.knmErknm?.durationDays === 'number'
          ? `${currentKnmData.knmErknm?.durationDays}`
          : undefined,
      durationHours:
        typeof currentKnmData.knmErknm?.durationHours === 'number'
          ? `${currentKnmData.knmErknm?.durationHours}`
          : undefined,
      approveRequired:
        currentKnmData.type?.code === KnmTypeCodesEnum.PP
          ? approveRequiredSelectOption
          : getApproveRequiredSelectOptionsByCreated(currentKnmData.created, settings).find(item => {
              if (initialReasons) {
                const reasonAgreement = initialReasons[0]?.reason?.type?.agreement;

                if (reasonAgreement && item.agreementValue === reasonAgreement) {
                  return item.agreementValue === reasonAgreement;
                }
              }

              return item.value === currentKnmData.knmErknm?.approve?.approveRequired;
            }),
      dataContent: currentKnmData.knmErknm?.dataContent || undefined,
      approved: isPlanType
        ? approvedPlanTypeSelectOption
        : (isShow22 ? approvedSelectOptions : approvedSelectOptionsOld).find(
            item => item.value === currentKnmData.knmErknm?.approve?.approved,
          ),
      approveNumberDecision: currentKnmData.knmErknm?.approve?.numberDecision || undefined,
      approveDateDecision: convertInitialKnmDateToFormDate(
        currentKnmData.knmErknm?.approve?.dateDecision,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      wasRejected: currentKnmData.knmErknm?.approve?.wasRejected || undefined,
      approveFioSigner: currentKnmData.knmErknm?.approve?.fioSigner || undefined,
      approveTitleSigner: currentKnmData.knmErknm?.approve?.titleSigner || undefined,
      approveAppeal: typeof isInitialApproveAppeal === 'boolean' ? isInitialApproveAppeal : undefined,
      approveNoteAppeal: currentKnmData.knmErknm?.approve?.noteAppeal || undefined,
      rejectReasonType: initialRejectReasonType
        ? createKnmRejectReasonTypeSelectOption(initialRejectReasonType)
        : undefined,
      rejectReasonNote: currentKnmData.knmErknm?.approve?.rejectReasonNote || null,
      approveFile: currentKnmData.knmErknm?.approve?.approveFile || undefined,
      approveDateRejectReason: convertInitialKnmDateToFormDate(
        currentKnmData.knmErknm?.approve?.dateRejectReason,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      approveNote: currentKnmData.knmErknm?.approve?.note || undefined,
      documentRequestDate: convertInitialKnmDateToFormDate(
        currentKnmData.knmErknm?.documentRequestDate,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      documentResponseDate: convertInitialKnmDateToFormDate(
        currentKnmData.knmErknm?.documentResponseDate,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      initialStopDate: convertInitialKnmDateToFormDate(
        currentKnmData.knmErknm?.initialStopDate,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      isRemote:
        booleanSelectOptions.find((item: IBooleanSelectOption) => item.value === currentKnmData.knmErknm?.isRemote) ||
        undefined,
      interactionCategory: currentKnmData.knmErknm?.interactionCategory
        ? {
            id: currentKnmData.knmErknm?.interactionCategory.id,
            name: currentKnmData.knmErknm?.interactionCategory.name,
            label: currentKnmData.knmErknm?.interactionCategory.name,
            value: currentKnmData.knmErknm?.interactionCategory.id,
            videoCallEnabled: currentKnmData.knmErknm?.interactionCategory.videoCallEnabled,
          }
        : undefined,
      videoCallInfo:
        currentKnmData.knmErknm?.videoCallInfo?.map(item => {
          return {
            ...item,
            showLinkLikeHypelink: true,
            listId: uuidv4(),
            dateTime:
              convertInitialKnmDateToFormDate(item.dateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS) || '',
          };
        }) || undefined,
      remoteNote: currentKnmData.knmErknm?.noteRemote || undefined,
      isSelection: booleanSelectOptions.find(
        (item: IBooleanSelectOption) => item.value === currentKnmData.knmErknm?.isSelection,
      ),
      selection: currentKnmData.knmErknm?.selection || undefined,
      selectionReactQuillEditor: handleReactQuillDeltaValue(
        currentKnmData.knmErknm?.selectionReactQuillEditor || currentKnmData.knmErknm?.selection,
      ),
      withVideo: booleanSelectOptions.find(
        (item: IBooleanSelectOption) => item.value === currentKnmData.knmErknm?.withVideo,
      ),
      isPresence: booleanSelectOptions.find(
        (item: IBooleanSelectOption) => item.value === currentKnmData.knmErknm?.isPresence,
      ),
      isPresenceDelegateFullNames: currentKnmData.knmErknm?.isPresenceDelegateFullNames?.map(item => ({
        value: item?.value ? item.value : undefined,
        guid: item.guid ? item.guid : undefined,
        listId: item?.guid || uuidv4(),
      })),
      returnSelection: booleanSelectOptions.find(
        (item: IBooleanSelectOption) => item.value === currentKnmData.knmErknm?.returnSelection,
      ),
      isHasCollaboratingOrganization:
        typeof isInitialIsHasCollaboratingOrganization === 'boolean'
          ? isInitialIsHasCollaboratingOrganization
          : undefined,
      isApprovedOnPaper: typeof isInitialIsApprovedOnPaper === 'boolean' ? isInitialIsApprovedOnPaper : undefined,
      isChecklistsUsed: typeof isInitialIsChecklistsUsed === 'boolean' ? isInitialIsChecklistsUsed : undefined,
      appealStatus: appealStatusSelectOptions.find(option => option.value === currentKnmData?.knmErknm?.appealStatus),
      isAppealed: typeof isInitialIsAppealed === 'boolean' ? isInitialIsAppealed : undefined,
      oldKnmNumber: currentKnmData.knmErknm?.oldKnmNumber || null,
      oldPlanNumber: currentKnmData.knmErknm?.oldPlanNumber || null,
      webNotificationInfos: currentKnmData.knmErknm?.webNotificationInfos || null,
      repeatPassports: currentKnmData.knmErknm?.repeatPassports || undefined,
      untimelyBasic: currentKnmData.knmErknm?.untimelyBasic || undefined,
      approveStatus: currentKnmData.knmErknm?.approve?.status || null,
      reasonDocuments: initialReasonDocuments?.map(reasonDocument => ({
        ...reasonDocument,
        type: reasonDocument?.type ? createKnmDocumentTypeSelectOption(reasonDocument?.type) : undefined,
        attachments: reasonDocument.attachments || undefined,
        violationEvidences: reasonDocument.videoFixations
          ? reasonDocument.videoFixations?.map(fix => ({
              guid: fix.guid || null,
              date: convertInitialKnmDateToFormDate(fix.dateVideo, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
              description: fix.descVideo,
              link: fix.linkVideo,
              originPassportId: fix.originPassportId,
              listId: uuidv4(),
              isNew: false,
            }))
          : undefined,
        listId: reasonDocument.guid || uuidv4(),
      })),
      approvedOnPaperDocuments: initialApprovedOnPaperDocuments
        ? {
            ...initialApprovedOnPaperDocuments,
            type: initialApprovedOnPaperDocuments.type
              ? createKnmDocumentTypeSelectOption(initialApprovedOnPaperDocuments.type)
              : undefined,
            attachments: initialApprovedOnPaperDocuments.attachments || undefined,
            listId: initialApprovedOnPaperDocuments.guid || uuidv4(),
          }
        : undefined,
      experts: initialExperts?.map(item => ({
        ...item,
        title: item.title || undefined,
        type: item.type ? createKnmExpertTypeSelectOption(item.type) : undefined,
        listId: item.guid || uuidv4(),
      })),
      serviceDocument: initialServiceDocument
        ? {
            type: initialServiceDocument.type || undefined,
            attachments: initialServiceDocument.attachments || undefined,
            guid: initialServiceDocument.guid || undefined,
            correlationToken: initialServiceDocument.correlationToken || undefined,
            description: initialServiceDocument.description || undefined,
            descriptionReactQuillEditor: handleReactQuillDeltaValue(
              initialServiceDocument.descriptionReactQuillEditor || initialServiceDocument.description,
            ),
          }
        : undefined,
      organizations: formOrganizations || [],
      acts: formActs,
      resultDecisions: formResultDecisions,
      judgments: initialJudgments
        ? convertKnmDataJudgmentsToFormValues(state, initialJudgments, formActs, formResultDecisions)
        : undefined,
      reasonsErknm: initialReasons?.map(item => {
        const initialReasonType = convertKnmDataConditionTypeToFormValue(state, KnmSystemTypesEnum.ERKNM, item?.reason);

        return {
          ...item,
          riskIndicators:
            item.riskIndicators?.map(indicatorValue => {
              const indicatorOption = indicatorValue.riskIndicator?.dictId
                ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_INDICATORY>(
                    state,
                    indicatorValue.riskIndicator?.dictId,
                    indicatorValue.riskIndicator?.dictVersionId,
                  )
                : undefined;

              return {
                guid: indicatorValue.guid,
                riskIndicator: indicatorOption,
                indicatorRiskAuto: indicatorValue.indicatorRiskAuto || undefined,
                indicatorRiskAttachment: indicatorValue.indicatorRiskAttachment || undefined,
                isNew: false,
              };
            }) || undefined,
          type:
            item?.reason && initialReasonType
              ? createKnmReasonExtendedSelectOption(
                  item?.reason,
                  { ...initialReasonType, dateBegin: ervkValue?.dateBegin || '', dateEnd: ervkValue?.dateEnd || '' },
                  item?.reason?.flagProfvizit,
                  item?.reason?.inspectionResults,
                )
              : undefined,
          text: item?.reason?.text || undefined,
          textReactQuillEditor: handleReactQuillDeltaValue(
            item?.reason?.textReactQuillEditor || item?.reason?.text || undefined,
          ),
          inspectionResults: item?.reason?.inspectionResults,
          ervkUuid: item.reason?.ervkUuid || undefined,
          title: item.reason?.title || undefined,
          shortTitle: item.reason?.shortTitle || undefined,
          date: convertInitialKnmDateToFormDate(item?.reason?.date, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          assignmentNumber: item?.reason?.assignmentNumber || undefined,
          assignmentDate: convertInitialKnmDateToFormDate(item?.reason?.assignmentDate, DATE_FORMAT_LONG_FNS),
          requirementDetails: item?.reason?.requirementDetails || undefined,
          programAttachment: item.reason?.programAttachment || undefined,
          programNumber: item.reason?.programNumber || undefined,
          fullName: getFullNameGuarantor(item.order?.guarantors[0]),
          dateRequirement:
            convertInitialKnmDateToFormDate(item?.reason?.dateRequirement, DATE_FORMAT_LONG_FNS) || undefined,
          links: item.reason?.links || undefined,
          passportLinks: item.reason?.passportLinks || undefined,
          violationEvidences: item?.reason?.videoFixations
            ? item?.reason?.videoFixations?.map(fix => ({
                guid: fix.guid || null,
                date: convertInitialKnmDateToFormDate(fix.dateVideo, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
                description: fix.descVideo,
                link: fix.linkVideo,
                originPassportId: fix.originPassportId,
                listId: uuidv4(),
                isNew: false,
              }))
            : undefined,
          listId: item?.numGuid || uuidv4(),
          order: item.order,
          reason: item.reason,
          lastConductedKnmDate: convertInitialKnmDateToFormDate(
            item?.reason?.lastConductedKnmDate,
            DATE_FORMAT_LONG_FNS,
          ),
        };
      }),
      eventsErknm: events,
      suspensionEvents,
      isDurationDaysDisabled: typeof currentKnmData.knmErknm?.durationDays === 'number',
      directDurationDays:
        typeof currentKnmData.knmErknm?.directDurationDays === 'number'
          ? `${currentKnmData.knmErknm?.directDurationDays}`
          : undefined,
      created: convertInitialKnmDateToFormDate(currentKnmData.created, DATE_FORMAT_ISO_FNS, DATE_FORMAT_ISO_FNS),
      requirementsNpaErknm: requirementsNpaValue,
      subjectNotifications:
        initialNotifications?.map(x => {
          const notification: IKnmNotificationsForm = {
            date: convertInitialKnmDateToFormDate(x.date, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
            type: x.type ? createKnmNotificationMethodSelectOption(x.type) : undefined,
            comment: x.comment,
            commentReactQuillEditor: null, // x.commentReactQuillEditor,
            listId: x.guid || uuidv4(),
            isNew: !!x.isNew,
            isDateEmpty: !x.date,
            isTypeEmpty: !x.type,
            isCommentEmpty: !x.comment,
          };

          return notification;
        }) || null,
      invalidFields: currentKnmData.knmCopyInvalidFields || undefined,
      isNeedApplyChangesInApprovedPlan: currentKnmData?.knmErknm?.approve?.isNeedApplyChangesInApprovedPlan || null,
      approveRemarks:
        initialApproveRemarks?.length && knmRemarkSelectOption.length
          ? convertKnmApproveRemarksToFormValues(initialApproveRemarks, knmRemarkSelectOption)
          : undefined,
      firstExtractDateTime:
        convertInitialKnmDateToFormDate(
          currentKnmData?.firstExtractDateTime,
          DATE_FORMAT_LONG_MINUTE_FNS,
          DATE_FORMAT_ISO_FNS,
        ) || null,
      agreements: convertAgreementsDataToFormValues(initialAgreements, state, formResultDecisions),
      agreementOnApproval: currentKnmData?.knmErknm?.agreementOnApproval || undefined,
      decisionSystemSignatureDateTime: currentKnmData?.knmErknm?.decision?.systemSignatureDateTime,
      decisionSystemSignatureMnemonic: currentKnmData?.knmErknm?.decision?.systemSignatureMnemonic,
      decisionSystemSignatureTitle: currentKnmData?.knmErknm?.decision?.systemSignatureTitle,
      decisionSystemSignatureUsed: currentKnmData?.knmErknm?.decision?.systemSignatureUsed,
    }),
  );
}

function* knmErknmInitLinksSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    const {
      data: { id, version, knmErknm },
    } = action.payload;

    if (typeof id === 'number' && version === KnmVersionsEnum.ERKNM && knmErknm) {
      const links: IKnmLink[] = yield fetchKnmLinks(id);

      yield put(
        knmErknmSetInitialFormValues({
          links,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmErknmInitProsecutorOrganizationSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmSystemType = getKnmSystemType(state);
    const isErknmKnmSystemType = knmSystemType === KnmSystemTypesEnum.ERKNM;

    if (isErknmKnmSystemType) {
      const { data: currentKnmData } = action.payload;
      const { prosecutorOrganization } = currentKnmData;

      if (prosecutorOrganization?.ervkUuid && prosecutorOrganization?.ervkVersionUuid) {
        const ervkProsecutorOrganization: IUserOrganisation = yield fetchProsecutorOrganisationsByErvkUuidAndVersion(
          prosecutorOrganization?.ervkUuid,
          prosecutorOrganization?.ervkVersionUuid,
        );

        const userOrganizationWithDistrictSelectOption = createUserOrganizationWithDistrictSelectOption(
          ervkProsecutorOrganization,
        );

        yield put(
          knmErknmSetInitialFormValues({
            prosecutorOrganizationErknm: userOrganizationWithDistrictSelectOption,
          }),
        );
      } else {
        const initialProsecutorOrganization = currentKnmData?.prosecutorOrganization;
        const initialDistrictName = currentKnmData?.district?.name || null;
        const initialDistrictId = currentKnmData?.district?.id || null;

        yield put(
          knmErknmSetInitialFormValues({
            prosecutorOrganizationErknm:
              initialProsecutorOrganization?.id &&
              !initialProsecutorOrganization?.ervkUuid &&
              initialProsecutorOrganization?.name
                ? createKnmProsecutorOrganisationSelectOption({
                    prosecutorOrganization: {
                      ...initialProsecutorOrganization,
                      id: initialProsecutorOrganization.id,
                      name: initialProsecutorOrganization?.name,
                    },
                    districtName: initialDistrictName,
                    districtId: initialDistrictId?.toString() || null,
                  })
                : undefined,
          }),
        );
      }
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmErknmFillAgreementProsecutorOrganizationSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    yield call(createDictionaryWaitChannel);

    const state: IRootState = yield select();

    const { data: currentKnmData } = action.payload;
    const { knmErknm } = currentKnmData;
    const initialFormValues = getKnmErknmInitialFormValues(state);

    if (knmErknm?.agreements) {
      const promises: Promise<IUserOrganisationSelectOption | undefined>[] = [];
      const organizations: IProsecutorOffice[] = [];

      // Загрузка данных о прокуратурах из справочников
      knmErknm?.agreements?.forEach(async i => {
        // Загрузка прокуратуры из основных сведений
        if (
          i.prosecutorOrganization &&
          !organizations.find(org =>
            org.ervkUuid
              ? org.ervkUuid === i.terminate?.prosecutorOrganization?.ervkUuid &&
                org.ervkVersionUuid === i.terminate?.prosecutorOrganization?.ervkVersionUuid
              : org.id && org.id === i.terminate?.prosecutorOrganization?.id,
          )
        ) {
          promises.push(getProsecutorOrganization(i.prosecutorOrganization, currentKnmData?.district));
          organizations.push(i.prosecutorOrganization);
        }
        // Загрузка прокуратуры из сведений о расторжении
        if (
          i.terminate?.prosecutorOrganization &&
          !organizations.find(org =>
            org.ervkUuid
              ? org.ervkUuid === i.terminate?.prosecutorOrganization?.ervkUuid &&
                org.ervkVersionUuid === i.terminate?.prosecutorOrganization?.ervkVersionUuid
              : org.id && org.id === i.terminate?.prosecutorOrganization?.id,
          )
        ) {
          promises.push(getProsecutorOrganization(i.terminate?.prosecutorOrganization, currentKnmData?.district));
          organizations.push(i.terminate?.prosecutorOrganization);
        }
      });

      const results: (IUserOrganisationSelectOption | undefined)[] = yield call(() => Promise.all(promises));

      const agreementsForm = initialFormValues.agreements?.map(agreement => {
        const findOrg = (prosecutorOrganization: IUserOrganisationSelectOption | undefined) =>
          results.find(
            org =>
              org &&
              (org.ervkUuid
                ? org.ervkUuid === prosecutorOrganization?.ervkUuid &&
                  org.ervkVersionUuid === prosecutorOrganization?.ervkVersionUuid
                : org.id && org.id === prosecutorOrganization?.id),
          );

        return {
          ...agreement,
          prosecutorOrganization: findOrg(agreement.prosecutorOrganization),
          terminate: !agreement.terminate
            ? undefined
            : {
                ...agreement.terminate,
                prosecutorOrganization: findOrg(agreement.terminate?.prosecutorOrganization),
              },
        } as IErknmFormAgreement;
      });

      yield put(
        knmErknmSetInitialFormValues({
          agreements: agreementsForm,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmErknmInitAdrogationLinkSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    const {
      data: { id, version, knmErknm },
    } = action.payload;

    if (
      typeof id === 'number' &&
      version === KnmVersionsEnum.ERKNM &&
      knmErknm &&
      knmErknm.abrogation &&
      knmErknm.abrogation.link
    ) {
      let pmType: IDictionaryVersion | undefined;
      let pmTypeValue: IKnmType | undefined;
      const links: IKnmLink[] = yield fetchKnmAbrogationLinks(id);
      const pmObject: { pmData?: IKnm; pmTypeValue?: IKnmType; ervkValue?: IKnmErvk } = {};
      const pmData: IKnm = yield fetchKnm(links[0].id);
      let ervkDictionary: IDictionaryVersion | undefined;
      let ervkValue: IKnmErvk | undefined;

      if (pmData.pmErknm && pmData.pmErknm?.pmType) {
        pmType = yield fetchDictionaryValue(pmData.pmErknm.pmType.dictId, pmData.pmErknm.pmType.dictVersionId);
      }

      if (pmType) {
        pmTypeValue = JSON.parse(pmType.value);
      }

      if (
        pmData.pmErknm?.reasons &&
        pmData.pmErknm?.reasons.length &&
        pmData.pmErknm?.reasons[0].reason &&
        pmData.pmErknm?.reasons[0].reason.ervkUuid
      ) {
        ervkDictionary = yield fetchDictionaryValue(
          DictionaryUUIDsEnum.ERVK,
          pmData.pmErknm?.reasons[0].reason.ervkUuid,
        );
      }

      if (ervkDictionary) {
        ervkValue = JSON.parse(ervkDictionary.value);
      }

      pmObject.pmData = pmData;
      pmObject.pmTypeValue = pmTypeValue;
      pmObject.ervkValue = ervkValue;

      yield put(
        knmErknmSetInitialFormValues({
          abrogation: {
            comment: knmErknm.abrogation.comment,
            reasonId: { id: parseInt(knmErknm.abrogation.reasonId || '0', 10) },
            reasonFile: knmErknm.abrogation.reasonFile ? [knmErknm.abrogation.reasonFile] : undefined,
            link: {
              ...knmErknm.abrogation.link,
              id: links.length ? links[0].id : -1,
            },
            pmObject,
          },
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmErknmInit(action: ActionType<typeof knmGetSuccess>) {
  if (action.payload.data.knmErknm && action.payload.data.version === KnmVersionsEnum.ERKNM) {
    const state: IRootState = yield select();
    const initHash = getInitHash(state);

    yield put(coreSetLoading(true));

    // Сбрасываем трекер справочников, чтобы «зависшие» с прошлого открытия
    // запросы не блокировали ожидание загрузки на 30 секунд
    globalDictionaryTracker.reset();

    yield fork(dictionaryHandlerSaga);
    yield knmErknmInitFormValuesSaga(action);
    yield knmErknmInitSelectOptionsSaga(action);
    yield knmErknmInitAdrogationLinkSaga(action);
    yield knmErknmSetValueByOptionsFromCatalogActionsSaga({
      type: CatalogsActionsEnum.CATALOG_GET_SUCCESS,
      payload: { catalogName: CommonCatalogsNamesEnum.DISTRICTS, data: [] },
    });
    yield knmErknmFillAgreementProsecutorOrganizationSaga(action);
    yield knmErknmInitProsecutorOrganizationSaga(action);
    if (initHash) {
      scrollToBlock(initHash);
      yield put(setInitHash(undefined));
    }

    yield put(coreSetLoading(false));
  }
}

function* knmErknmFillOrganizationsAndObjectsByOtherPassportSaga(
  action: ActionType<typeof knmFillOrganizationsAndObjectsByOtherPassport>,
) {
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);
  const formRef = getKnmFormRef(state);
  const initialFormValues = getKnmErknmInitialFormValues(state);

  const { formOrganizations } = yield getOrganizations(
    currentKnmData?.id,
    currentKnmData?.created,
    action.payload.value.organizations.map(org => ({ ...org, isFilledFromLink: true })),
    state,
  );
  const list = getDistrictSelectOptions(state);
  const currentDictionariesFromData: IKnmDictionary[] = [];
  const objects: IKnmErknmFormObject[] = [];

  const regionFallbacks: Array<{
    region?: IKnmDistrictSelectOption;
    fiasRegion?: IKnmDistrictSelectOption;
  }> = yield call(async () => {
    const sourceObjects = action.payload.value.objects || [];

    return Promise.all(
      sourceObjects.map(async object => {
        if (object?.region || object?.fiasRegion) {
          return {};
        }

        if (object.notInFIAS) {
          return {
            region: await findRegionSelectOption(object?.manualAddress || object?.address, null, list),
          };
        }

        return {
          fiasRegion: await findRegionSelectOption(object?.address, null, list),
        };
      }),
    );
  });

  // После await стор мог обновиться — перечитываем, чтобы не брать устаревший state
  const freshState: IRootState = yield select();

  (action.payload.value.objects || []).forEach((object, index) => {
    let objRegion: IKnmDistrictSelectOption | undefined;
    let objFiasRegion: IKnmDistrictSelectOption | undefined;

    const linkObjRegionId = typeof object?.region === 'string' ? object?.region : object?.region?.id?.toString();
    const linkObjFiasRegionId =
      typeof object?.fiasRegion === 'string' ? object?.fiasRegion : object?.fiasRegion?.id?.toString();

    // Смотрим Местонахождение в сведениях об объектах контроля
    if (object?.region) {
      objRegion = list.find(i => i.id.toString() === linkObjRegionId);
    }
    if (object?.fiasRegion) {
      objFiasRegion = list.find(i => i.id.toString() === linkObjFiasRegionId);
    }
    // Тот же фолбэк, что и при штатной загрузке паспорта: у связанного паспорта
    // субъект может быть не заполнен, тогда выводим его из адреса
    if (!object?.region && !object?.fiasRegion) {
      objRegion = regionFallbacks[index]?.region;
      objFiasRegion = regionFallbacks[index]?.fiasRegion;
    }
    if (object.objectType) {
      currentDictionariesFromData.push(object.objectType);
    }
    if (object.objectKind) {
      currentDictionariesFromData.push(object.objectKind);
    }
    if (object.objectSubKind) {
      currentDictionariesFromData.push(object.objectSubKind);
    }
    if (object.riskCategory) {
      currentDictionariesFromData.push(object.riskCategory);
    }
    if (object.dangerClass) {
      currentDictionariesFromData.push(object.dangerClass);
    }

    const objectTypeSelectOption = object.objectType?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_TYPE>(
          freshState,
          object.objectType?.dictId,
          object.objectType?.dictVersionId,
        )
      : undefined;
    const objectKindSelectOption = object.objectKind?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
          freshState,
          object.objectKind?.dictId,
          object.objectKind?.dictVersionId,
        )
      : undefined;
    const objectSubKindSelectOption = object.objectSubKind?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
          freshState,
          object.objectSubKind?.dictId,
          object.objectSubKind?.dictVersionId,
        )
      : undefined;
    const riskCategorySelectOption = object.riskCategory?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_CATEGORY>(
          freshState,
          object.riskCategory?.dictId,
          object.riskCategory?.dictVersionId,
        )
      : undefined;
    const dangerClassSelectOption = object.dangerClass?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.DANGER_CLASS>(
          freshState,
          object.dangerClass?.dictId,
          object.dangerClass?.dictVersionId,
        )
      : undefined;

    objects.push({
      region: objRegion,
      fiasRegion: objFiasRegion,
      objectType: objectTypeSelectOption,
      objectKind: objectKindSelectOption,
      objectSubKind: objectSubKindSelectOption,
      riskCategory: riskCategorySelectOption,
      dangerClass: dangerClassSelectOption,
      address: object.address || undefined,
      guid: object.guid || undefined,
      ervkNumber: object.ervkNumber || undefined,
      ervkId: object.ervkId || undefined,
      cosId: object.cosId || undefined,
      cosVersion: object.cosVersion || undefined,
      name: object.name || undefined,
      streetGuid: object.streetGuid || undefined,
      houseGuid: object.houseGuid || undefined,
      roomGuid: object.roomGuid || undefined,
      steadGuid: object.steadGuid || undefined,
      addressFns: object.addressFns || undefined,
      autocomplete: typeof object.autocomplete === 'boolean' ? object.autocomplete : undefined,
      dateCreate: object.dateCreate || undefined,
      geolocation: object.geolocation || undefined,
      listId: object.guid || uuidv4(),
      isNew: false,
      manualAddress: object.manualAddress || undefined,
      notInFIAS: object.notInFIAS || undefined,
      regionName: object.regionName || undefined,
      isErrorFiasValue: undefined,
      inspectionResults: object.inspectionResults || undefined,
      organizationErvkId: object.organizationErvkId || undefined,
      updating: object.updating || undefined,
      isFilledFromLink: object.isFilledFromLink || undefined,
    });
  });

  const initialEvents = action.payload.value.events;
  const events = initialEvents?.map((item, index) => {
    if (item.event) currentDictionariesFromData.push(item.event);

    const selectOption = item.event?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
          state,
          item.event?.dictId,
          item.event?.dictVersionId,
        )
      : undefined;
    const formEvent: IKnmErknmFormEvent = {
      type: selectOption && {
        ...selectOption,
        inspectionResult: item.event?.inspectionResult,
      },
      ...(!selectOption && initialFormValues.eventsErknm?.[index]),
      guid: item.guid,
      startDate: convertInitialKnmDateToFormDate(item.startDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      stopDate: convertInitialKnmDateToFormDate(item.stopDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      dateCreate: item.dateCreate,
      listId: item?.guid || uuidv4(),
      interactionCategory: item?.interactionCategory
        ? createInteractionCategorySelectOption(item.interactionCategory)
        : undefined,
      inspectors: item.inspectors
        ? item.inspectors.map(x => {
            const eventInspectorForm: IKnmErknmFormEventInspector = {
              guid: x.guid,
              fullName: createKnmEventInspectorSelectOption(x.fullName, x.guid),
            };

            return eventInspectorForm;
          })
        : undefined,
      isNew: false,
      submittedDocuments: createSubmittedDocuments(item.submittedDocuments),
      originalStopDate: convertInitialKnmDateToFormDate(
        item.originalStopDate,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      isFilledFromLink: item.isFilledFromLink || undefined,
    };

    return formEvent;
  });

  const requirementsNpaValue = action.payload.value.requirements?.map<IKnmErknmFormRequirementNpa>(item => ({
    guid: uuidv4(),
    npa: {
      id: item.npaId,
      number: item.number || undefined,
      acceptanceDate: formatDate({
        date: item.acceptanceDate ?? '',
        dateFormat: DATE_FORMAT_LONG_FNS,
        parseFormat: SYSTEM_DATE_FORMAT_FNS,
      }),
      title: item.title || undefined,
    },
    structuralUnit: {
      id: item.structuralUnit.id,
      title: item.structuralUnit.title || undefined,
    },
    mandatory: item.structuralUnit.mandatoryRequirements
      ? item.structuralUnit.mandatoryRequirements.map(mandatory => ({
          mandatory: {
            id: mandatory.id,
            title: mandatory.title || '',
          },
          category: mandatory.category ? createMandatoryCategorySelectOption(mandatory.category) : undefined,
          violations: mandatory.violations?.length ? convertKnmDataViolationsToFormValues(mandatory.violations) : null,
        }))
      : [],
    inspectionResults: item.inspectionResults,
    inspectionResult: undefined,
    isFilledFromLink: item.isFilledFromLink || undefined,
  }));

  const currentFormValues = formRef?.current.getState().values;

  yield put(
    knmErknmSetInitialFormValues({
      ...currentFormValues,
      organizations: formOrganizations || [],
      objectsErknm: objects,
      eventsErknm: events,
      requirementsNpaErknm: requirementsNpaValue,
      reasonsErknm: !initialFormValues.reasonsErknm
        ? undefined
        : [
            {
              ...initialFormValues.reasonsErknm[0],
              links: [action.payload.link],
            },
          ],
    }),
  );

  if (currentKnmData?.knmErknm) {
    const orgs = action.payload.value.organizations.map(i => ({ ...i, act: null, resultDecisions: null }));
    const data = {
      knmErknm: {
        ...currentKnmData.knmErknm,
        organizations: orgs,
        objects: action.payload.value.objects,
      },
    };

    if (action.payload.value.events) {
      data.knmErknm.events = action.payload.value.events;
    }
    yield put(knmPatchRawData(data));
  }

  const resultArrayActions: unknown[] = [];

  uniq(currentDictionariesFromData).forEach(item => resultArrayActions.push(getDictionariesItemValue(item)));
  yield all(resultArrayActions);
}

function* knmErknmSetValueByOptionsFromCatalogActionsSaga(action: ActionType<typeof catalogGetSuccess>) {
  const { catalogName } = action.payload;
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);
  const initialObjects = currentKnmData?.knmErknm?.objects;
  const initialPlaces = currentKnmData?.knmErknm?.places;
  const initialEvents = currentKnmData?.knmErknm?.events;
  const initialFormValues = getKnmErknmInitialFormValues(state);

  switch (catalogName) {
    case CommonCatalogsNamesEnum.DISTRICTS: {
      let mainRegion: IKnmDistrictSelectOption | undefined;
      let mainFiasRegion: IKnmDistrictSelectOption | undefined;

      const list = getDistrictSelectOptions(state);

      // Смотрим Место вынесения решения
      if (currentKnmData?.knmErknm?.decision?.region) {
        mainRegion = list.find(i => i.id.toString() === currentKnmData?.knmErknm?.decision?.region);
      }
      if (currentKnmData?.knmErknm?.decision?.fiasRegion) {
        mainFiasRegion = list.find(i => i.id.toString() === currentKnmData?.knmErknm?.decision?.fiasRegion);
      }

      const events = initialEvents?.map((item, index) => {
        const selectOption = item.event?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
              state,
              item.event?.dictId,
              item.event?.dictVersionId,
            )
          : undefined;
        const formEvent: IKnmErknmFormEvent = {
          type: selectOption,
          ...(!selectOption && initialFormValues.eventsErknm?.[index]),
          guid: item.guid,
          startDate: convertInitialKnmDateToFormDate(item.startDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          stopDate: convertInitialKnmDateToFormDate(item.stopDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          dateCreate: item.dateCreate,
          listId: item?.guid || uuidv4(),
          interactionCategory: item?.interactionCategory
            ? createInteractionCategorySelectOption(item.interactionCategory)
            : undefined,
          inspectors: item.inspectors
            ? item.inspectors.map(x => {
                const eventInspectorForm: IKnmErknmFormEventInspector = {
                  guid: x.guid,
                  fullName: createKnmEventInspectorSelectOption(x.fullName, x.guid),
                };

                return eventInspectorForm;
              })
            : undefined,
          isNew: false,
        };

        return formEvent;
      });

      const suspensionEvents: IKnmErknmFormSuspensionEvent[] = [];

      initialEvents?.forEach((event, index) => {
        suspensionEvents.push(...convertSuspensionEventsToFormData(event, events?.[index], index, state));
      });

      sortSuspensionEvents(suspensionEvents);

      const regionFallbacks: Array<{
        region?: IKnmDistrictSelectOption;
        fiasRegion?: IKnmDistrictSelectOption;
      }> = yield call(async () => {
        if (!initialObjects?.length) {
          return [];
        }

        return Promise.all(
          initialObjects.map(async obj => {
            if (obj?.region || obj?.fiasRegion) {
              return {};
            }

            if (obj.notInFIAS) {
              return {
                region: await findRegionSelectOption(obj?.manualAddress || obj?.address, null, list),
              };
            }

            return {
              fiasRegion: await findRegionSelectOption(obj?.address, null, list),
            };
          }),
        );
      });

      // После await стор мог обновиться — перечитываем, чтобы не брать устаревший state
      const freshState: IRootState = yield select();
      const freshInitialFormValues = getKnmErknmInitialFormValues(freshState);

      yield put(
        knmErknmSetInitialFormValues({
          region: mainRegion,
          fiasRegion: mainFiasRegion,
          objectsErknm: initialObjects?.map((obj, index) => {
            let objRegion: IKnmDistrictSelectOption | undefined;
            let objFiasRegion: IKnmDistrictSelectOption | undefined;

            // Смотрим Местонахождение в сведениях об объектах контроля
            if (obj?.region) {
              if (typeof obj.region === 'object') {
                objRegion = list.find(i => i.id.toString() === (obj.region as IFederalDistrict)?.id?.toString());
              } else {
                objRegion = list.find(i => i.id.toString() === obj?.region);
              }
            }
            if (obj?.fiasRegion) {
              if (typeof obj.fiasRegion === 'object') {
                objFiasRegion = list.find(
                  i => i.id.toString() === (obj.fiasRegion as IFederalDistrict)?.id?.toString(),
                );
              } else {
                objFiasRegion = list.find(i => i.id.toString() === obj?.fiasRegion);
              }
            }
            if (!obj?.region && !obj?.fiasRegion) {
              objRegion = regionFallbacks[index]?.region;
              objFiasRegion = regionFallbacks[index]?.fiasRegion;
            }

            const initialObject: IKnmErknmFormObject | undefined = freshInitialFormValues.objectsErknm?.[index];

            return {
              ...getInitialObject(initialObject),
              tmpErvkCosId: obj.tmpErvkCosId,
              region: objRegion,
              fiasRegion: objFiasRegion,
            };
          }),
          suspensionEvents,
          places: initialPlaces?.map(obj => {
            let objRegion: IKnmDistrictSelectOption | undefined;
            let objFiasRegion: IKnmDistrictSelectOption | undefined;

            // Смотрим Местонахождение в сведениях об объектах контроля
            if (obj?.region) {
              if (typeof obj.region === 'object') {
                objRegion = list.find(i => i.id.toString() === (obj.region as IFederalDistrict)?.id?.toString());
              } else {
                objRegion = list.find(i => i.id.toString() === obj?.region);
              }
            }
            if (obj?.fiasRegion) {
              if (typeof obj.fiasRegion === 'object') {
                objFiasRegion = list.find(
                  i => i.id.toString() === (obj.fiasRegion as IFederalDistrict)?.id?.toString(),
                );
              } else {
                objFiasRegion = list.find(i => i.id.toString() === obj?.fiasRegion);
              }
            }

            return {
              value: obj.value || undefined,
              guid: obj.guid,
              dateCreate: obj.dateCreate,
              listId: obj.guid || uuidv4(),
              manualAddress: obj.manualAddress || undefined,
              notInFIAS: obj.notInFIAS || undefined,
              region: objRegion,
              fiasRegion: objFiasRegion,
              matchesObjectAddress: obj.matchesObjectAddress || undefined,
            };
          }),
        }),
      );
      break;
    }
    case CommonCatalogsNamesEnum.KNM_REMARKS: {
      const knmRemarkSelectOption = getCatalogKnmRemarkSelectOptions(state);
      const initialApproveRemarks = currentKnmData?.knmErknm?.approve?.remarks || null;

      yield put(
        knmErknmSetInitialFormValues({
          approveRemarks: convertKnmApproveRemarksToFormValues(initialApproveRemarks, knmRemarkSelectOption),
        }),
      );

      break;
    }
    default:
  }
}

function* knmErknmUpdateEvidencesByDecisionLinksSaga(action: ActionType<typeof knmUpdateEvidencesByDecisionLinks>) {
  function* funcTry() {
    const { data } = action.payload;
    const evidencesFieldName = 'reasonsErknm[0].violationEvidences';

    const state: IRootState = yield select();
    const formRef = getKnmFormRef(state);
    // @ts-ignore
    const currentEvidences: IKnmErknmViolationEvidence[] =
      formRef?.current.getFieldState(evidencesFieldName as keyof IKnmForm)?.value || [];

    const customEvidences = currentEvidences?.filter(item => !item.originPassportId);

    // Параллельное выполнение всех запросов
    const results: IDecision[] = yield all(data.map(item => call(fetchDecisionById, item.id)));

    const handleDecisionResponse = (decision: IDecision): IKnmErknmViolationEvidence[] | undefined => {
      return getDecisionEvidences(decision);
    };

    // Обработка результатов
    const newDecisionEvidences: IKnmErknmViolationEvidence[] = yield all(
      results.map((decision: IDecision) => call(handleDecisionResponse, decision)).filter(Boolean),
    );

    const result = [...(newDecisionEvidences?.flatMap(item => item) || []), ...customEvidences];

    if (result?.length > 0 && formRef?.current) {
      // @ts-ignore
      formRef?.current?.change(evidencesFieldName as keyof IKnmForm, result);
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}
